import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '../context/UserContext';
import QAPriorityQueue from '../components/QAPriorityQueue';
import { canOverrideQueue } from '../utils/qaPriority';
import axios from 'axios';
import SearchableSelect from '../components/SearchableSelect';
import { EditableCell } from '../components/InlineEditTable';
import ExportConfigPanel from '../components/ExportConfigPanel';
import StatusBreakdownTable from '../components/StatusBreakdownTable';
import { goalsToStatusBreakdownRows } from '../utils/statusBreakdown';
import { LEAD_STATUS_COLORS, effectiveStatus, underReviewBadge } from '../utils/leadStatus';
import { matchesColFilter, NOTE_FILTER_OPTIONS, matchesNoteFilter, noteDate, noteByline } from '../utils/leadNoteFilters';
import { describeAcceptResult } from '../utils/deliveryNotices';
import { isBlockedByHoldOrCancel } from '../utils/deliveryCancel';
import { buildStatusGroupMap, buildProgramOptionGroups, closedProgramIdSet, buildComponentOptionGroups, buildGoalOptionGroups } from '../utils/statusGroups';

const API = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Style constants (matches LeadUpload.js conventions)
// ---------------------------------------------------------------------------
const thStyle = {
  padding: '7px 12px', textAlign: 'left', borderBottom: '1px solid #aed6f1',
  fontWeight: '600', color: '#1a6da8', fontSize: '12px', userSelect: 'none',
  whiteSpace: 'nowrap', background: '#eaf4fb', overflow: 'hidden',
  position: 'sticky', top: 0, zIndex: 2,
};
const tdStyle = {
  padding: '7px 12px', borderBottom: '1px solid #eaf2fb', fontSize: '12px', color: '#333',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '4px', display: 'block', fontWeight: '600' };
const inputStyle = {
  padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
};
const btnStyle = (color, disabled) => ({
  padding: '6px 14px', background: disabled ? '#bdc3c7' : color,
  color: 'white', border: 'none', borderRadius: '4px',
  cursor: disabled ? 'default' : 'pointer', fontWeight: '600', fontSize: '12px',
});

// ---------------------------------------------------------------------------
// Map QA check name → column key(s) it corresponds to.
// Used to highlight the specific cell that failed a check.
// ---------------------------------------------------------------------------
const CHECK_TO_COLS = {
  email_syntax:     ['email'],
  business_email:   ['email'],
  duplicate:        ['email'],
  suppression:      ['email'],
  geo:              ['state', 'country'],
  segment:          ['segment'],
  industry:         ['industry'],
  employee_size:    ['number_of_employees', 'actual_company_size'],
  tal:              ['company'],
  phone:            ['phone'],
  title_keywords:   ['title'],
  asset_alignment:  ['asset_downloaded'],
  consent:          ['privacy_policy_checked__c', 'single_opt_in__c'],
  name_email_match: ['name', 'email'],
  address:          ['address'],
  domain_cap:       ['company_domain'],
  carryover_list:   ['email', 'company_domain'],
};

// Returns a Set of column keys that have a QA flag on this lead.
// Display-only formatting for a real DateTime column (e.g. digital_reg_datetime,
// call_confirm_datetime) — backend returns an ISO string; just make it readable.
function formatDateTimeDisplay(v) {
  if (!v) return null;
  return v.replace('T', ' ').slice(0, 16);
}

// Display-only formatting for optin_date, a real Date column — backend
// returns an ISO "YYYY-MM-DD" string (needed as-is for the native
// type="date" input's own value), but NeTrA's own date convention
// everywhere else (exports, delivery) is MM/DD/YYYY, not ISO order. Real
// report, 2026-09-16: this column showed "2026-09-10" with no reformatting
// at all, reading as a wrong/converted date when the underlying value was
// actually correct — only the display order was never fixed up.
function formatDateDisplay(v) {
  if (!v) return null;
  const [y, m, d] = v.split('-');
  return (y && m && d) ? `${m}/${d}/${y}` : v;
}

function flaggedCols(lead) {
  const cols = new Set();
  if (!lead.qa_flags) return cols;
  for (const flag of lead.qa_flags) {
    const checkName = flag.split(':')[0].trim();
    for (const col of (CHECK_TO_COLS[checkName] || [])) cols.add(col);
  }
  return cols;
}

// Synthesizes an explanatory reason for a Rejected lead that has no computed QA
// check flags — surfaces WHY it's rejected (upload status / reject_reason /
// PM override note) instead of leaving the Flags column looking empty/"not
// run". Not written to lead.qa_flags — display-only, derived from fields the
// lead already carries. Priority: explicit PM override note > reject_reason
// (mapped from either a manual CSV upload's own reject_reason column, or
// Convertr's 2ndCheckFlagReason — labeled "Manual" since it's usually
// PM-provided context, not automated QA output) > plain "uploaded as
// Rejected" with no other explanation.
// Two flavors of "Rejected" — same Lead.status, distinguished by qa_override.
// "Client Rejected" means the client's own delivery-feedback flagged it (set
// automatically by the Check Feedback bulk-apply on LeadDelivery.js, or
// manually via this same override choice here); anything else — an explicit
// "Reject" override, or an auto-QA rejection with no override at all — is
// treated as DemandArc's own call ("by DA"). See backend leads.py's
// OVERRIDE_TO_STATUS for the full rationale.
const rejectedStatusLabel = (lead) =>
  lead.qa_override === 'Client Rejected' ? 'Rejected - by Client' : 'Rejected - by DA';

function rejectReasonFlag(lead) {
  const isRejected = lead.qa_override === 'Reject' || lead.qa_override === 'Client Rejected'
    || (!lead.qa_override && lead.status === 'Rejected');
  if (!isRejected) return null;
  if (lead.qa_override_by) return `Override: ${lead.qa_override_note || '(no note)'}`;
  if (lead.reject_reason) return `Manual reject reason: ${lead.reject_reason}`;
  return 'Rejected via lead status upload';
}

// Cell highlight style based on lead's qa_status.
const CELL_HIGHLIGHT = {
  Rejected:          { background: '#fdf0ee', borderLeft: '3px solid #e74c3c' },
  'Needs Review':    { background: '#fef9e7', borderLeft: '3px solid #f0c040' },
  'Client Returned': { background: '#fef0e6', borderLeft: '3px solid #d35400' },
};

// ---------------------------------------------------------------------------
// Column definitions — key matches lead field, always/optional
// ---------------------------------------------------------------------------
const ALL_COLUMNS = [
  { key: 'lead_id',               label: 'Lead ID',        always: true  },
  { key: 'name',                  label: 'Name',           always: true  },
  { key: 'email',                 label: 'Email',          always: true  },
  { key: 'title',                 label: 'Job_Title',      always: false },
  { key: 'title_simplified',      label: 'Job_Level',      always: false },
  { key: 'job_function',          label: 'Job Function',   always: false },
  { key: 'company',               label: 'Company',        always: true  },
  { key: 'company_domain',        label: 'Company Domain', always: false },
  { key: 'city',                  label: 'City',           always: false },
  { key: 'state',                 label: 'State',          always: false },
  { key: 'country',               label: 'Country',        always: false },
  { key: 'website',               label: 'Website',        always: false },
  { key: 'segment',               label: 'Segment',        always: false },
  { key: 'industry',              label: 'Industry',       always: false },
  { key: 'naics_code',            label: 'NAICS Code',     always: false },
  { key: 'number_of_employees',      label: 'Emp. Size',      always: false },
  { key: 'actual_company_size',      label: 'Actual Co. Size',always: false },
  { key: 'company_revenue',          label: 'Company Revenue',always: false },
  { key: 'phone',                    label: 'Phone',          always: false },
  { key: 'asset_downloaded',         label: 'Asset',          always: false },
  { key: 'privacy_policy_checked__c',label: 'Privacy Consent',always: false },
  { key: 'single_opt_in__c',         label: 'Opt-In',         always: false },
  { key: 'address',                  label: 'Address',        always: false },
  { key: 'address_2',                label: 'Address_2',      always: false },
  { key: 'title_category',            label: 'Title Category', always: false },
  { key: 'qa_status',                label: 'QA Status',      always: true  },
  { key: 'qa_flags',                 label: 'Flags',          always: true  },
  { key: 'supplier_delivery_date',   label: 'Delivery Date',  always: false },
  { key: 'optin_date',                label: 'OptIn_Date',     always: false },
  { key: 'mobile_phone',             label: 'Mobile Phone',   always: false },
  { key: 'postal_code',              label: 'Postal Code',    always: false },
  { key: 'mkto_person_notes',        label: 'Mkto Notes',     always: false },
  { key: 'linkedin_profile',         label: 'LinkedIn',       always: false },
  { key: 'linkedin_company_url',     label: 'Company LinkedIn', always: false },
  { key: 'utm_source__c',            label: 'UTM Source',     always: false },
  { key: 'utm_medium__c',            label: 'UTM Medium',     always: false },
  { key: 'utm_campaign__c',          label: 'UTM Campaign',   always: false },
  { key: 'campaign_notes__c',        label: 'Campaign Notes', always: false },
  { key: 'convertr_accepted',              label: 'Convertr 2nd Check',      always: false },
  { key: 'convertr_current_status',        label: 'Convertr Status',         always: false },
  { key: 'convertr_flag_reason_category',  label: 'Convertr Flag Category',  always: false },
  { key: 'convertr_flag_comment',          label: 'Convertr Flag Comment',   always: false },
  { key: 'over_allocation',                label: 'Over Allocation',         always: false },
  { key: 'reject_reason',                  label: 'Reject Reason',           always: false },
  // Marketo program-membership feedback (POST /lead-delivery/feedback-check,
  // written onto the Lead by matching lead_id — see backend CLAUDE.md's
  // "Marketo feedback" section) — same never-toggled-on-by-default treatment
  // as the Convertr feedback columns above; MARKETO_FEEDBACK_KEYS below
  // auto-adds these to visibleCols only once real data for them shows up.
  { key: 'marketo_progression_status',     label: 'Marketo Status',          always: false },
  { key: 'marketo_is_exhausted',           label: 'Marketo Exhausted',       always: false },
  { key: 'marketo_acquired_by',            label: 'Marketo Acquired By',     always: false },
  { key: 'marketo_reached_success',        label: 'Marketo Reached Success', always: false },
  { key: 'marketo_membership_date',        label: 'Marketo Membership Date', always: false },
  { key: 'asset_downloaded_del__c',  label: 'Asset Del.',     always: false },
  { key: 'asset_download_date__c',   label: 'Asset DL Date',  always: false },
  { key: 'sub_industry__c',          label: 'Sub-Industry',   always: false },
  { key: 'client_delivery_number',   label: 'Client Del #',   always: false },
  { key: 'upload_qa_notes',          label: 'Upload QA Notes',always: false },
  // Buying Signals (BANT + qualification) — migration 0066
  { key: 'budget',                   label: 'Budget',         always: false },
  { key: 'authority',                label: 'Authority',      always: false },
  { key: 'need',                     label: 'Need',           always: false },
  { key: 'timing',                   label: 'Timing',         always: false },
  { key: 'buying_stage',             label: 'Buying Stage',   always: false },
  { key: 'buying_role',              label: 'Buying Role',    always: false },
  { key: 'digital_reg_datetime',     label: 'Digital Reg. Date/Time', always: false },
  { key: 'call_confirm_datetime',    label: 'Call Confirm Date/Time', always: false },
  { key: 'call_need_driving_interest', label: 'Call - Need Driving Interest', always: false },
  { key: 'best_time_to_follow_up',   label: 'Best Time to Follow-Up', always: false },
  { key: 'call_notes',               label: 'Call Notes',    always: false },
  { key: 'dial_path',                label: 'Dial Path',      always: false },
  // 10 generic client-specific slots (e.g. "Fortune 500") — see backend
  // CLAUDE.md's "Custom Fields" note. Label here is only the fallback shown
  // until a program names the slot via QA Rules' Required Fields client
  // alias — see fieldAliasMap/colLabel() below for the actual substitution.
  { key: 'custom_field_1',           label: 'Custom Field 1', always: false },
  { key: 'custom_field_2',           label: 'Custom Field 2', always: false },
  { key: 'custom_field_3',           label: 'Custom Field 3', always: false },
  { key: 'custom_field_4',           label: 'Custom Field 4', always: false },
  { key: 'custom_field_5',           label: 'Custom Field 5', always: false },
  { key: 'custom_field_6',           label: 'Custom Field 6', always: false },
  { key: 'custom_field_7',           label: 'Custom Field 7', always: false },
  { key: 'custom_field_8',           label: 'Custom Field 8', always: false },
  { key: 'custom_field_9',           label: 'Custom Field 9', always: false },
  { key: 'custom_field_10',          label: 'Custom Field 10', always: false },
];
const DEFAULT_VISIBLE = new Set(ALL_COLUMNS.filter(c => c.always).map(c => c.key));
const MARKETO_FEEDBACK_KEYS = [
  'marketo_progression_status', 'marketo_is_exhausted', 'marketo_acquired_by',
  'marketo_reached_success', 'marketo_membership_date',
];

// Default pixel widths per column key.
const COL_DEFAULT_WIDTHS = {
  qa_status: 130, qa_flags: 260, name: 140, email: 190, company: 140,
  lead_id: 80, title: 170, title_simplified: 130, job_function: 140, state: 80, country: 90, segment: 120,
  industry: 130, number_of_employees: 100, actual_company_size: 120, company_revenue: 130,
  phone: 130, asset_downloaded: 170, privacy_policy_checked__c: 110,
  single_opt_in__c: 80, address: 170, address_2: 150, title_category: 110,
  supplier_delivery_date: 120, optin_date: 110, company_domain: 160,
  client_delivery_number: 110, upload_qa_notes: 220,
  budget: 100, authority: 120, need: 160, timing: 110,
  buying_stage: 130, buying_role: 130,
  digital_reg_datetime: 150, call_confirm_datetime: 150,
  call_need_driving_interest: 220, best_time_to_follow_up: 170, call_notes: 240, dial_path: 140,
  campaign_notes__c: 260,
  marketo_progression_status: 150, marketo_is_exhausted: 120, marketo_acquired_by: 120,
  marketo_reached_success: 140, marketo_membership_date: 150,
};

// Long, often multi-paragraph free-text columns that need a real textarea to
// edit (preserves line breaks, room to work) instead of the generic
// single-line editable cell — see the render branch below.
const LONG_TEXT_COLS = new Set(['call_notes', 'campaign_notes__c']);
// The two LinkedIn columns share one cell treatment (value plus a leading ↗ link-out);
// only the hover text differs. linkedin_company_url is opt-in per program (Armis).
const LINKEDIN_COLS = {
  linkedin_profile: 'Open LinkedIn profile in a new tab',
  linkedin_company_url: 'Open company LinkedIn page in a new tab',
};

// Smart default order: status/flags first → core identity → flagged cols promoted → rest.
function computeSmartOrder(visibleKeys, leads) {
  const PRIORITY = ['qa_status', 'qa_flags', 'name', 'email', 'company'];
  const flaggedSet = new Set();
  leads.forEach(l => flaggedCols(l).forEach(c => flaggedSet.add(c)));
  const promoted = [...flaggedSet].filter(c => visibleKeys.has(c) && !PRIORITY.includes(c));
  const handled = new Set([...PRIORITY, ...promoted]);
  const rest = ALL_COLUMNS.map(c => c.key).filter(k => visibleKeys.has(k) && !handled.has(k));
  return [...PRIORITY, ...promoted, ...rest].filter(k => visibleKeys.has(k));
}

// ---------------------------------------------------------------------------
// QA status badge — shows the Under Review sub-badge (Not Run / Needs Review
// / Ready → target / Client Returned) for a lead still Under Review, or the
// plain lifecycle status for anything already terminal/pre-accept. See
// src/utils/leadStatus.js — lead.status alone decides which of those two
// this renders, it's never ambiguous.
// ---------------------------------------------------------------------------
function QABadge({ lead, includesCallReview }) {
  const c = lead.status === 'Under Review'
    ? underReviewBadge(lead, includesCallReview)
    : {
        label: lead.status === 'Rejected' ? rejectedStatusLabel(lead) : lead.status,
        ...(LEAD_STATUS_COLORS[lead.status] || { bg: '#f5f5f5', color: '#888', border: '#ddd' }),
      };
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: '10px', padding: '2px 9px', fontSize: '11px', fontWeight: '600',
      whiteSpace: 'nowrap',
    }}>
      {lead.qa_override && lead.qa_override !== 'Needs Review' && lead.status === 'Under Review' ? `${c.label} ✎` : c.label}
    </span>
  );
}

// Options for the inline QA Status dropdown (main table cell). "Approved
// Pending Call Review" only makes sense for a component that has call
// review turned on — appended conditionally rather than hardcoded, same
// gating as the "Approved Pending Call Review" tab/column elsewhere on this
// page (see includesCallReview). Backend validates this too (400s if the
// lead's own component doesn't have it enabled) — this is just keeping the
// option from appearing where it'd never be accepted.
const buildOverrideOptions = (includesCallReview) => [
  { value: '', label: '— Auto QA —' },
  { value: 'Client Accepted', label: '✓ Client Accepted' },
  { value: 'Client Returned', label: '↩ Client Returned – Replacement Needed' },
  { value: 'Needs Review', label: '⚑ Needs Review – Revisit Later' },
  { value: 'Accept', label: 'PM Accept' },
  { value: 'Reject', label: 'Rejected - by DA' },
  { value: 'Client Rejected', label: 'Rejected - by Client' },
  ...(includesCallReview ? [{ value: 'Approved Pending Call Review', label: '📞 Approved Pending Call Review' }] : []),
];

const rollupTdStyle = { padding: '5px 10px', fontSize: '12px', textAlign: 'right', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' };

// A `?status=` deep-link only ever needs validating against this literal set —
// unlike ?program_id (an id that must be checked against the fetched programs
// list before it's safe to apply), a status tab key is a fixed string with no
// async load to wait on, so it can be applied straight into qaFilter's initial
// state. 'Pending Review' is deliberately NOT included — GET /leads/ never
// returns a Pending Review lead at all (see this file's own "No 'Pending
// Review' tab on purpose" note below), so there is no tab for it to select;
// My Home's status summary bar sends its Pending Review chip to the Pending
// Deliveries section instead, not here.
const VALID_STATUS_PARAMS = new Set([
  'Under Review', 'Approved Pending Call Review', 'DemandArc Accepted', 'Client Accepted', 'Rejected',
]);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const LEVEL_LABELS = { program: 'Program', component: 'Component', goal: 'Goal' };

export default function LeadQA() {
  const location = useLocation();
  // Deep-link support: My Home's task CTAs open this page with ?program_id=
  // in the URL (window.open() can't carry React Router's in-memory `state`
  // across a new tab — see TargetingLists.js's selectListId for the same
  // dual-support pattern with a same-tab navigate() caller). Consumed once
  // the programs list has actually loaded, below.
  const programIdParam = new URLSearchParams(location.search).get('program_id');
  const pendingProgramId = useRef(
    location.state?.program_id || (programIdParam ? Number(programIdParam) : null)
  );
  // Same deep-link idiom as program_id above, for My Home's status summary
  // bar — ?status=Under+Review, etc. Applied straight into qaFilter's initial
  // state (no fetched-list validation needed, see VALID_STATUS_PARAMS above).
  const statusParam = new URLSearchParams(location.search).get('status');
  const [programs, setPrograms] = useState([]);
  const [components, setComponents] = useState([]);
  const [goals, setGoals] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [showQueue, setShowQueue] = useState(false);   // the "View QA Priority Queue" popup
  const [selectedComponent, setSelectedComponent] = useState('');
  const [selectedGoal, setSelectedGoal] = useState('');
  const [qaFilter, setQaFilter] = useState(
    () => (statusParam && VALID_STATUS_PARAMS.has(statusParam)) ? statusParam : 'all'
  );
  // Delivery filter, shown on every status tab — null means "not explicitly
  // chosen yet" (treated as 'all', see activeDeliverySelection below); 'all' or
  // a specific delivery_id once the PM picks from the dropdown. The choice
  // carries across tabs, so one delivery can be looked at under each status;
  // on a tab where that delivery has no leads it falls back to 'all'.
  const [deliverySelection, setDeliverySelection] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollupGoals, setRollupGoals] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkOverride, setBulkOverride] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkClearNote, setBulkClearNote] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);
  const [acceptingQaStatuses, setAcceptingQaStatuses] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [rerunMsg, setRerunMsg] = useState(null);
  const [visibleCols, setVisibleCols] = useState(new Set(DEFAULT_VISIBLE));
  // {field_key: client_alias} for this program, from Required Fields' client-alias
  // setting (QA Rules) — used by colLabel() below to show e.g. "Fortune 500"
  // instead of the generic "Custom Field 1" fallback wherever that field's label
  // renders (table header, column picker, "Export This View" CSV headers). Most
  // relevant for the 10 generic custom_field_N slots, but works for any field.
  const [fieldAliasMap, setFieldAliasMap] = useState({});
  const [colOrder, setColOrder]       = useState([]);   // ordered array of visible col keys
  const [colWidths, setColWidths]     = useState({});   // { [key]: px width }
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [savedColumnPresets, setSavedColumnPresets] = useState([]);
  // Which Component Rollup rows are expanded to show their nested goals inline —
  // keyed by component id, multi-expand allowed (same shape as Programs.js's
  // Drilldown `expandedComponents`). Only meaningful at the program-level rollup;
  // reset whenever the selector scope changes so a stale expansion from a
  // previous program/component doesn't carry over.
  const [expandedRollupRows, setExpandedRollupRows] = useState({});
  const [newPresetName, setNewPresetName] = useState('');
  const [presetBusy, setPresetBusy] = useState(false);
  const [presetError, setPresetError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const { currentUser } = useCurrentUser();
  const [colFilters, setColFilters] = useState({});
  const [noteFilter, setNoteFilter] = useState('any');
  const [exportingFull, setExportingFull] = useState(false);
  const [exportingAccepted, setExportingAccepted] = useState(false);
  const [showFullMenu, setShowFullMenu] = useState(false);
  const [showAcceptedMenu, setShowAcceptedMenu] = useState(false);
  const [acceptedIncludeDemandArc, setAcceptedIncludeDemandArc] = useState(true);
  const [acceptedIncludeClient, setAcceptedIncludeClient] = useState(true);
  const [acceptedPromotedOnly, setAcceptedPromotedOnly] = useState(false);
  const [acceptedFormat, setAcceptedFormat] = useState('csv');
  // '' = use the current page-level scope (program/component/goal selector)
  // as normal; a goal id here overrides just this one export to that single
  // goal, without changing what's shown in the table or the top selectors.
  // Added for SailPoint, which sources each goal from a different upload —
  // a PM needs a per-goal Accepted list even while viewing the whole program.
  const [acceptedGoalId, setAcceptedGoalId] = useState('');
  // Same idea, one level up — scopes the download to a single component
  // instead of a single goal. Mutually exclusive with acceptedGoalId (picking
  // one clears the other); goal wins if somehow both are set.
  const [acceptedComponentId, setAcceptedComponentId] = useState('');
  const [exportFields, setExportFields] = useState([]);      // from GET /leads/export-fields
  // Per-export-type default config: {full, accepted} -> config|null. Rejected/Returned
  // dropped 2026-08-11 — the PM no longer manually passes those files to the supplier,
  // so their buttons (and ExportConfigPanel's per-type defaults for them) were removed.
  const [exportDefaults, setExportDefaults] = useState({ full: null, accepted: null });
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [convertrSyncOpen, setConvertrSyncOpen] = useState(false);
  const [convertrFile, setConvertrFile] = useState(null);
  const [convertrSyncing, setConvertrSyncing] = useState(false);
  const [convertrResult, setConvertrResult] = useState(null);
  // Deliveries sitting at status "Pending" (not yet accepted) for the program
  // currently in scope — surfaced as an "accept and start QA?" banner so a PM
  // who navigates straight here (skipping My Home's task queue) doesn't need a
  // separate trip through Pending Deliveries first. Accepting moves this
  // delivery's Pending Review leads to Under Review and runs QA in one step,
  // same as clicking through a My Home Scrub task.
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [acceptingDeliveryId, setAcceptingDeliveryId] = useState(null);
  const dragColRef   = useRef(null);
  // Header double-click auto-fit: a canvas for measuring text, and a timer
  // that holds a header's single-click sort briefly so a double-click
  // (which also fires two clicks) auto-fits without flipping the sort.
  const measureCanvasRef = useRef(null);
  const sortClickTimerRef = useRef(null);

  // Derived: most granular selection determines level + param
  const level = selectedGoal ? 'goal' : selectedComponent ? 'component' : selectedProgram ? 'program' : null;
  const entityId = selectedGoal || selectedComponent || selectedProgram;
  const apiParam = level === 'program' ? 'program_id' : level === 'component' ? 'component_id' : 'goal_id';

  // Raw full lists
  const [allComponents, setAllComponents] = useState([]);
  const [allGoals, setAllGoals] = useState([]);

  // Closed programs nest into a collapsible section on the Program picker below
  // instead of mixing into the flat list — see src/utils/statusGroups.js. Same
  // treatment for Component/Goal pickers, inherited from their owning program.
  const [programStatuses, setProgramStatuses] = useState([]);
  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const programOptionGroups = buildProgramOptionGroups(programs, programStatusGroupMap);
  const closedProgramIds = closedProgramIdSet(programs, programStatusGroupMap);
  const componentByIdForGoals = new Map(allComponents.map(c => [c.id, c]));
  const componentOptionGroups = buildComponentOptionGroups(components, closedProgramIds);
  const goalOptionGroups = buildGoalOptionGroups(goals, componentByIdForGoals, closedProgramIds);

  // Load all three lists on mount
  useEffect(() => {
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data || [])).catch(() => {});
    axios.get(`${API}/components/`).then(r => { setAllComponents(r.data || []); setComponents(r.data || []); }).catch(() => {});
    axios.get(`${API}/goals/`).then(r => { setAllGoals(r.data || []); setGoals(r.data || []); }).catch(() => {});
    axios.get(`${API}/leads/export-fields`).then(r => setExportFields(r.data || [])).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data || [])).catch(() => {});
  }, []);

  // Deep-link: select the requested program once it's actually in the
  // fetched list (see pendingProgramId above).
  useEffect(() => {
    if (pendingProgramId.current && programs.some(p => p.id === pendingProgramId.current)) {
      setSelectedProgram(String(pendingProgramId.current));
      pendingProgramId.current = null;
    }
  }, [programs]);

  // Export configs are program-scoped — resolve the owning program_id regardless
  // of whether the current selector level is program/component/goal.
  const resolvedProgramId = level === 'program'
    ? selectedProgram
    : level === 'component'
      ? allComponents.find(c => String(c.id) === String(selectedComponent))?.program_id
      : level === 'goal'
        ? allComponents.find(c => String(c.id) === String(allGoals.find(g => String(g.id) === String(selectedGoal))?.component_id))?.program_id
        : null;

  // Whether the current selection needs the "Includes Call Review" toggle — adds a 6th
  // status ("Approved Pending Call Review") to the tab bar and rollup, and changes what
  // "Ready" previews as on the Under Review badge. This is a component-level flag now
  // (not program-level), so: a specific component/goal selection uses that one
  // component's own flag; a program-level (or no) selection is a union across every
  // component under it — the table/tabs are shared across components at that level, so
  // the column set has to cover whichever of them need it (a component without the flag
  // just shows 0 there, same "safe superset" treatment used elsewhere in this file). See
  // src/utils/leadStatus.js's getLeadStatuses()/underReviewBadge().
  const includesCallReview = level === 'component'
    ? !!allComponents.find(c => String(c.id) === String(selectedComponent))?.includes_call_review
    : level === 'goal'
      ? !!allComponents.find(c => String(c.id) === String(allGoals.find(g => String(g.id) === String(selectedGoal))?.component_id))?.includes_call_review
      : allComponents.some(c => String(c.program_id) === String(resolvedProgramId) && c.includes_call_review);

  // Load the program's per-export-type default configs whenever the resolved
  // program changes; falls back to all-null (legacy "all fields, NetRA labels").
  useEffect(() => {
    const empty = { full: null, accepted: null, rejected: null, returned: null };
    // The Accepted dropdown's per-goal/per-component export override only
    // makes sense for whatever program is currently in view — reset both on
    // every program change so a goal/component id from a previous program
    // can never silently ride along into a different one's export.
    setAcceptedGoalId('');
    setAcceptedComponentId('');
    if (!resolvedProgramId) { setExportDefaults(empty); return; }
    axios.get(`${API}/export-configs/defaults`, { params: { program_id: resolvedProgramId } })
      .then(r => setExportDefaults({ ...empty, ...(r.data || {}) }))
      .catch(() => setExportDefaults(empty));
  }, [resolvedProgramId]);

  // Default the visible-columns set to the always-visible baseline plus
  // whatever fields this program actually tracks via Required Fields (any
  // tier — Required/Requested/Optional — see QARules.js), so a PM lands on
  // a column set that already matches the supplier template instead of an
  // empty picker. Recomputed (not merged) on every resolvedProgramId change
  // — switching programs re-defaults to that program's own tracked fields
  // rather than carrying over a previous program's leftover manual toggles;
  // a manual toggle or a loaded Saved Column View still wins for the rest
  // of that program's session, since this only fires on program change.
  useEffect(() => {
    if (!resolvedProgramId) { setVisibleCols(new Set(DEFAULT_VISIBLE)); setFieldAliasMap({}); return; }
    axios.get(`${API}/qa-rules/program/${resolvedProgramId}/required-fields-summary`)
      .then(r => {
        const trackedKeys = new Set();
        const aliasMap = {};
        (r.data || []).forEach(group => (group.fields || []).forEach(f => {
          trackedKeys.add(f.key);
          if (f.client_alias) aliasMap[f.key] = f.client_alias;
        }));
        setVisibleCols(new Set([...DEFAULT_VISIBLE, ...trackedKeys]));
        setFieldAliasMap(aliasMap);
      })
      .catch(() => { setVisibleCols(new Set(DEFAULT_VISIBLE)); setFieldAliasMap({}); });
  }, [resolvedProgramId]);

  // "New leads pending — accept and start QA?" banner: a PM who lands here
  // directly (rather than clicking through a My Home Scrub task) still needs
  // a way to know a delivery is sitting unaccepted for the program in view.
  // Scoped to resolvedProgramId so it covers whichever level (program/
  // component/goal) is actually selected. held_for_resubmission deliveries
  // are excluded — a batch the supplier flagged to fix isn't ready for QA yet
  // (same exclusion My Home's own pending-deliveries list applies).
  const loadPendingDeliveries = useCallback(() => {
    if (!resolvedProgramId) { setPendingDeliveries([]); return; }
    axios.get(`${API}/supplier-deliveries/`, { params: { program_id: resolvedProgramId } })
      .then(r => setPendingDeliveries(
        (r.data || []).filter(d => d.status === 'Pending' && !d.held_for_resubmission)
      ))
      .catch(() => setPendingDeliveries([]));
  }, [resolvedProgramId]);

  useEffect(() => { loadPendingDeliveries(); }, [loadPendingDeliveries]);

  // Same accept-and-start-QA action a My Home Scrub task's CTA triggers
  // (POST /supplier-deliveries/{id}/accept moves this delivery's Pending
  // Review leads to Under Review and runs QA in one step) — just reachable
  // from directly inside Lead QA Review instead of only from the task queue.
  //
  // Re-checks held_for_resubmission immediately before accepting, since
  // loadPendingDeliveries() only fetches once on mount — a supplier can flag
  // a delivery held or cancelled after the banner already rendered. The
  // accept endpoint now refuses both too (409, 2026-10-29: PMs were accepting
  // bad uploads from this banner); this pre-check just avoids the round trip
  // for the common stale-render case and the catch below covers the race.
  const handleAcceptPendingDelivery = (deliveryId) => {
    setAcceptingDeliveryId(deliveryId);
    axios.get(`${API}/supplier-deliveries/`, { params: { program_id: resolvedProgramId } })
      .then(r => {
        const current = (r.data || []).find(d => d.id === deliveryId);
        if (!current || current.held_for_resubmission) {
          setPendingDeliveries(prev => prev.filter(d => d.id !== deliveryId));
          window.alert('This delivery is now held for resubmission — it was not accepted. It will be ready to accept once the supplier releases it.');
          setAcceptingDeliveryId(null);
          return null;
        }
        return axios.post(`${API}/supplier-deliveries/${deliveryId}/accept`)
          .then(res => {
            const outcome = describeAcceptResult(res.data);
            if (outcome.warn) window.alert(outcome.text);
            setPendingDeliveries(prev => prev.filter(d => d.id !== deliveryId));
            return reloadLeadsPreservingScroll();
          })
          .finally(() => setAcceptingDeliveryId(null));
      })
      .catch(err => {
        if (isBlockedByHoldOrCancel(err)) {
          // The server refuses a held or cancelled delivery (409, plain-language reason): show it and
          // refresh the banner so the delivery drops out instead of inviting another click.
          window.alert(err.response.data?.detail || 'This delivery can no longer be accepted.');
          loadPendingDeliveries();
        } else {
          window.alert('Failed to accept delivery — please try again.');
        }
        setAcceptingDeliveryId(null);
      });
  };

  // Auto-show the Marketo feedback columns the moment any lead in scope
  // actually has data for them — additive only (never removes a column the
  // PM already toggled on/off), so a program with no Marketo feedback yet
  // stays uncluttered while one that's been through Check Feedback surfaces
  // it automatically instead of requiring a manual toggle.
  useEffect(() => {
    const hasMarketoFeedback = leads.some(l => MARKETO_FEEDBACK_KEYS.some(k => l[k] != null));
    if (hasMarketoFeedback) {
      setVisibleCols(prev => new Set([...prev, ...MARKETO_FEEDBACK_KEYS]));
    }
  }, [leads]);

  // Cascade: program selection filters components + goals
  useEffect(() => {
    if (!selectedProgram) { setComponents(allComponents); setGoals(allGoals); return; }
    const filteredComps = allComponents.filter(c => String(c.program_id) === String(selectedProgram));
    setComponents(filteredComps);
    const compIds = new Set(filteredComps.map(c => String(c.id)));
    setGoals(allGoals.filter(g => compIds.has(String(g.component_id))));
    if (selectedComponent && !filteredComps.find(c => String(c.id) === String(selectedComponent))) {
      setSelectedComponent(''); setSelectedGoal(''); setLeads([]);
    }
  }, [selectedProgram, allComponents, allGoals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascade: component selection filters goals
  useEffect(() => {
    if (!selectedComponent) {
      if (selectedProgram) {
        const compIds = new Set(allComponents.filter(c => String(c.program_id) === String(selectedProgram)).map(c => String(c.id)));
        setGoals(allGoals.filter(g => compIds.has(String(g.component_id))));
      } else { setGoals(allGoals); }
      return;
    }
    const filteredGoals = allGoals.filter(g => String(g.component_id) === String(selectedComponent));
    setGoals(filteredGoals);
    if (selectedGoal && !filteredGoals.find(g => String(g.id) === String(selectedGoal))) {
      setSelectedGoal(''); setLeads([]);
    }
  }, [selectedComponent, allComponents, allGoals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Column Settings is a modal now (overlay click-to-close), so no outside-click
  // listener is needed the way the old popover required.
  const refreshColumnPresets = () => {
    axios.get(`${API}/lead-qa-column-presets/`)
      .then(r => setSavedColumnPresets(r.data || []))
      .catch(() => setSavedColumnPresets([]));
  };
  useEffect(() => { refreshColumnPresets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCurrentAsPreset = async () => {
    if (!newPresetName.trim() || !orderedVisibleCols.length) return;
    setPresetBusy(true); setPresetError(null);
    try {
      await axios.post(`${API}/lead-qa-column-presets/`, {
        name: newPresetName.trim(),
        column_keys: orderedVisibleCols.map(c => c.key),
      });
      setNewPresetName('');
      refreshColumnPresets();
    } catch (e) {
      setPresetError(e.response?.data?.detail || 'Save failed');
    }
    setPresetBusy(false);
  };

  const loadColumnPreset = (preset) => {
    const alwaysKeys = ALL_COLUMNS.filter(c => c.always).map(c => c.key);
    const savedKeys = (preset.column_keys || []).filter(k => ALL_COLUMNS.some(c => c.key === k));
    const finalOrder = [...alwaysKeys.filter(k => !savedKeys.includes(k)), ...savedKeys];
    setVisibleCols(new Set(finalOrder));
    setColOrder(finalOrder);
  };

  const deleteColumnPreset = async (id) => {
    if (!window.confirm('Delete this saved column view? This removes it for everyone.')) return;
    setPresetBusy(true);
    try {
      await axios.delete(`${API}/lead-qa-column-presets/${id}`);
      refreshColumnPresets();
    } catch (e) {
      setPresetError(e.response?.data?.detail || 'Delete failed');
    }
    setPresetBusy(false);
  };

  const clearSelections = () => {
    setSelectedProgram(''); setSelectedComponent(''); setSelectedGoal('');
    setComponents(allComponents); setGoals(allGoals); setLeads([]); setSelected(new Set());
  };

  // Load leads when entity changes
  const loadLeads = useCallback(() => {
    if (!entityId) { setLeads([]); setRollupGoals([]); return Promise.resolve(); }
    setLoading(true);
    setSelected(new Set());
    const leadsPromise = axios.get(`${API}/leads/`, { params: { [apiParam]: entityId } })
      .then(r => { setLeads(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    // Fetch title split summary (may return null if no title_keywords rule configured)
    // Fetch goals with counts for rollup — skip when a specific goal is selected (single row, no rollup needed)
    if (level !== 'goal') {
      const countParam = level === 'program' ? 'program_id' : 'component_id';
      axios.get(`${API}/goals/`, { params: { [countParam]: entityId, include_counts: true } })
        .then(r => setRollupGoals(r.data || []))
        .catch(() => setRollupGoals([]));
    } else {
      setRollupGoals([]);
    }
    return leadsPromise;
  }, [entityId, apiParam, level]);

  // Bulk actions (bulk override, rerun) refetch the whole list — unlike a single-lead
  // override, we don't know the new qa_status/qa_flags client-side, so a real reload
  // is unavoidable. Preserve scroll position across it so reviewing a long list doesn't
  // keep bouncing back to the top after every batch action.
  const reloadLeadsPreservingScroll = useCallback(() => {
    const scrollY = window.scrollY;
    return loadLeads().then(() => {
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    });
  }, [loadLeads]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  // Reset which rollup rows are expanded whenever the selector scope changes,
  // so a stale expansion from a previous program/component never carries over.
  useEffect(() => { setExpandedRollupRows({}); }, [entityId, level]);

  // Load saved column order + widths when entity changes; reset when cleared.
  useEffect(() => {
    if (!entityId) { setColOrder([]); setColWidths({}); return; }
    try {
      const savedOrder = JSON.parse(localStorage.getItem(`nqa_order_${entityId}`) || 'null');
      if (Array.isArray(savedOrder) && savedOrder.length) {
        // Keep only keys that still exist; prepend any always-visible cols not yet in saved order.
        const valid = savedOrder.filter(k => ALL_COLUMNS.find(c => c.key === k));
        const alwaysKeys = ALL_COLUMNS.filter(c => c.always).map(c => c.key);
        const missing = alwaysKeys.filter(k => !valid.includes(k));
        setColOrder([...missing, ...valid]);
      } else {
        setColOrder([]); // will be set when leads arrive
      }
    } catch { setColOrder([]); }
    try {
      const savedWidths = JSON.parse(localStorage.getItem(`nqa_widths_${entityId}`) || 'null');
      if (savedWidths && typeof savedWidths === 'object') setColWidths(savedWidths);
    } catch { setColWidths({}); }
  }, [entityId]); // eslint-disable-line

  // Compute smart order the first time leads arrive (if no saved/custom order yet).
  useEffect(() => {
    if (!leads.length || colOrder.length > 0) return;
    setColOrder(computeSmartOrder(visibleCols, leads));
  }, [leads]); // eslint-disable-line

  // Persist column order whenever it changes.
  useEffect(() => {
    if (!entityId || !colOrder.length) return;
    localStorage.setItem(`nqa_order_${entityId}`, JSON.stringify(colOrder));
  }, [colOrder, entityId]);

  // Persist column widths whenever they change.
  useEffect(() => {
    if (!entityId || !Object.keys(colWidths).length) return;
    localStorage.setItem(`nqa_widths_${entityId}`, JSON.stringify(colWidths));
  }, [colWidths, entityId]);

  // A column's displayed label — this program's client alias (Required Fields)
  // if one's set for the field, compounded onto the generic ALL_COLUMNS
  // label as "{label} ({alias})", matching QARules.js's own labelFor() for
  // template downloads. Real bug, 2026-08-19 (SailPoint): this used to
  // return the alias *alone*, so a well-known field like asset_downloaded
  // ("Asset") with a client alias set ("asset_title_1") lost its real label
  // entirely — a PM scanning the column picker for "Asset" would never
  // recognize the cryptic alias-only text as the same field, so it read as
  // "missing" even though it was present and checked. The 10 generic
  // custom_field_1..10 slots are the one deliberate exception: they have no
  // meaningful label of their own ("Custom Field 1" is a placeholder), so
  // the alias (e.g. "Fortune 500") *is* the name, not a clarifying suffix.
  const colLabel = (col) => {
    const alias = fieldAliasMap[col.key];
    if (!alias) return col.label;
    if (col.key.startsWith('custom_field_')) return alias;
    return `${col.label} (${alias})`;
  };

  // ---------------------------------------------------------------------------
  // Derived: filtered leads
  // ---------------------------------------------------------------------------
  const getColValue = (lead, key) => {
    if (key === 'name') return `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
    if (key === 'qa_status') return (
      lead.status === 'Under Review' ? underReviewBadge(lead, includesCallReview).label
      : lead.status === 'Rejected' ? rejectedStatusLabel(lead)
      : lead.status
    ) || '';
    if (key === 'qa_flags') return [...(lead.qa_flags || []), rejectReasonFlag(lead), lead.qa_override_note, lead.qa_override_note_by_name].filter(Boolean).join(' ');
    // Booleans need their own branch — `lead[key] || ''` would render `false`
    // as an empty string, indistinguishable from null/never-checked.
    if (key === 'marketo_is_exhausted' || key === 'marketo_acquired_by' || key === 'marketo_reached_success') {
      return lead[key] === true ? 'Yes' : lead[key] === false ? 'No' : '';
    }
    return String(lead[key] || '');
  };

  // Distinct delivery batches present among the CURRENT TAB's leads (grouped by
  // delivery_id, the real FK — not client_delivery_number, which a PM can
  // freely edit). Backs the delivery picker below. Sorted newest first by
  // supplier_delivery_date; manually-uploaded leads (delivery_id null) never
  // appear here — "All" is the only way to see those.
  const deliveryPoolLeads = leads.filter(l => qaFilter === 'all' || l.status === qaFilter);
  const deliveryOptions = [];
  {
    const byDelivery = {};
    deliveryPoolLeads.forEach(l => {
      if (l.delivery_id == null) return;
      if (!byDelivery[l.delivery_id]) {
        byDelivery[l.delivery_id] = { id: l.delivery_id, count: 0, date: null, label: null };
      }
      const d = byDelivery[l.delivery_id];
      d.count += 1;
      d.label = l.client_delivery_number || `Delivery ${l.delivery_id}`;
      if (l.supplier_delivery_date && (!d.date || l.supplier_delivery_date > d.date)) d.date = l.supplier_delivery_date;
    });
    deliveryOptions.push(...Object.values(byDelivery).sort((a, b) =>
      (b.date || '').localeCompare(a.date || '') || b.id - a.id
    ));
  }
  // null (not yet explicitly chosen) defaults to 'all' — a PM sees every lead
  // on the tab until they explicitly pick one delivery batch. A remembered
  // delivery with no leads on this tab also falls back to 'all', so switching
  // tabs can never strand the PM on an empty list with no obvious cause.
  const activeDeliverySelection = (
    deliverySelection !== null && (deliverySelection === 'all' || deliveryOptions.some(d => d.id === deliverySelection))
  ) ? deliverySelection : 'all';

  // Which of the 5 lifecycle statuses a lead is bucketed under — a plain
  // equality on lead.status, mirroring the backend's rollup exactly (see
  // src/utils/leadStatus.js). qa_status/qa_override no longer decide tab
  // membership, only the per-lead badge shown within "Under Review".
  const filteredLeads = leads
    .filter(l => qaFilter === 'all' || l.status === qaFilter)
    .filter(l => activeDeliverySelection === 'all' || l.delivery_id === activeDeliverySelection)
    .filter(l => matchesNoteFilter(l, noteFilter, currentUser ? currentUser.id : null))
    .filter(l => Object.entries(colFilters).every(([key, val]) =>
      matchesColFilter(getColValue(l, key), val)
    ))
    .sort((a, b) => {
      if (!sortField) return 0;
      const av = getColValue(a, sortField).toLowerCase();
      const bv = getColValue(b, sortField).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  const filtersActive = Object.values(colFilters).some(v => v) || noteFilter !== 'any';
  const clearAllFilters = () => { setColFilters({}); setNoteFilter('any'); };

  const handleSort = (key) => {
    if (sortField === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(key); setSortDir('asc'); }
  };

  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setColOrder(o => o.filter(k => k !== key));
      } else {
        next.add(key);
        // Append newly shown column after the last promoted/core col in smart order
        setColOrder(o => o.includes(key) ? o : [...o, key]);
      }
      return next;
    });
  };

  // Shared drag-reorder logic — used by both the table header drag handles and
  // the Column Settings modal's reorderable list, so there's one splice-based
  // reorder implementation instead of two copies drifting apart.
  const reorderCols = (fromKey, toKey) => {
    if (!fromKey || fromKey === toKey) return;
    setColOrder(prev => {
      const order = prev.length > 0 ? [...prev] : orderedVisibleCols.map(c => c.key);
      const fi = order.indexOf(fromKey), ti = order.indexOf(toKey);
      if (fi < 0 || ti < 0) return order;
      const next = [...order];
      next.splice(fi, 1);
      next.splice(ti, 0, fromKey);
      return next;
    });
  };

  // Double-click a column header (or its resize handle) to fit the column to
  // its widest content — header label included — across the rows currently
  // shown. Cells are single-line (tdStyle is nowrap), so the widest text is
  // the natural width. Measured with a canvas in the table's own font rather
  // than by reading DOM cells, since every column type renders differently.
  // Capped so one long notes cell can't produce a page-wide column.
  const AUTO_FIT_MAX_PX = 800;
  const autoFitColumn = (colKey) => {
    const col = orderedVisibleCols.find(c => c.key === colKey);
    if (!col) return;
    const canvas = measureCanvasRef.current || (measureCanvasRef.current = document.createElement('canvas'));
    const ctx = canvas.getContext('2d');
    const family = window.getComputedStyle(document.body).fontFamily || 'sans-serif';
    ctx.font = `600 12px ${family}`;
    let widest = ctx.measureText(String(colLabel(col))).width + 20; // + sort arrow
    ctx.font = `12px ${family}`;
    for (const lead of filteredLeads) {
      const v = getColValue(lead, colKey);
      if (v === null || v === undefined || v === '') continue;
      const w = ctx.measureText(Array.isArray(v) ? v.join(', ') : String(v)).width;
      if (w > widest) widest = w;
    }
    // 24px cell padding + room for in-cell icons/badges (LinkedIn ↗, status pills).
    const width = Math.min(AUTO_FIT_MAX_PX, Math.max(60, Math.ceil(widest + 24 + 16)));
    setColWidths(prev => ({ ...prev, [colKey]: width }));
  };

  const handleHeaderClick = (colKey) => {
    clearTimeout(sortClickTimerRef.current);
    sortClickTimerRef.current = setTimeout(() => handleSort(colKey), 220);
  };
  const handleHeaderDoubleClick = (colKey) => {
    clearTimeout(sortClickTimerRef.current);
    autoFitColumn(colKey);
  };
  // Resize handler — key-based, not index-based, so reordering doesn't break widths.
  const startColResize = (colKey, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[colKey] || COL_DEFAULT_WIDTHS[colKey] || 120;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (me) => setColWidths(prev => ({ ...prev, [colKey]: Math.max(60, startW + me.clientX - startX) }));
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ---------------------------------------------------------------------------
  // Selection helpers
  // ---------------------------------------------------------------------------
  const toggleSelect = (id) =>
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const selectAll = () =>
    setSelected(selected.size === filteredLeads.length ? new Set() : new Set(filteredLeads.map(l => l.id)));

  // ---------------------------------------------------------------------------
  // Inline field edit — PATCH /leads/{id}/fields, update local state on success
  // ---------------------------------------------------------------------------
  const handleEditField = useCallback(async (leadId, fields) => {
    // Merge the server's response, not the raw fields sent — matters for a date/
    // datetime field, where what a PM types (e.g. "07/15/2026") and the normalized
    // value the backend actually parsed/stored can differ.
    const res = await axios.patch(`${API}/leads/${leadId}/fields`, fields);
    setLeads(prev => prev.map(l =>
      l.id === leadId ? { ...l, ...res.data } : l
    ));
  }, []); // eslint-disable-line

  // ---------------------------------------------------------------------------
  // Inline QA override edit — PATCH /leads/{id}/qa-override, sends BOTH override
  // and note every time (the endpoint fully overwrites both fields, not a partial
  // patch), so an inline edit to just one always carries the other's current
  // value along untouched. Shared by the inline QA Status dropdown and the
  // inline Flags-column note field.
  // ---------------------------------------------------------------------------
  const handleOverrideSave = useCallback(async (leadId, override, note) => {
    const res = await axios.patch(`${API}/leads/${leadId}/qa-override`, {
      qa_override: override || null,
      qa_override_note: note || null,
    });
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ...res.data } : l));
    return res.data;
  }, []); // eslint-disable-line


  // ---------------------------------------------------------------------------
  // Bulk override
  // ---------------------------------------------------------------------------
  const handleBulkOverride = async () => {
    if (!bulkOverride || selected.size === 0) return;
    setBulkSaving(true);
    setBulkMsg(null);
    try {
      // qa_override_note is only included when the PM typed a note to apply
      // or explicitly checked "clear existing notes" — omitted entirely
      // otherwise, so the backend's explicit-presence gate leaves each
      // selected lead's own note untouched by default. Never wipe a note as
      // a side effect of the note field just being left blank.
      const res = await axios.post(`${API}/leads/bulk-qa-override`, {
        lead_ids: [...selected],
        qa_override: bulkOverride,
        ...(bulkNote ? { qa_override_note: bulkNote } : bulkClearNote ? { qa_override_note: null } : {}),
      });
      setBulkMsg({ ok: true, text: `${res.data.updated} lead${res.data.updated !== 1 ? 's' : ''} updated.` });
      setSelected(new Set());
      setBulkOverride('');
      setBulkNote('');
      setBulkClearNote(false);
      reloadLeadsPreservingScroll();
    } catch (e) {
      setBulkMsg({ ok: false, text: e.response?.data?.detail || 'Bulk override failed' });
    }
    setBulkSaving(false);
  };

  // Only shown on the "Under Review" tab: promotes each selected lead's own
  // already-computed effective status (qa_override → status → qa_status —
  // whatever its badge already shows, not one value forced onto everyone like
  // the Override control above) into a permanent Lead.status, so it moves out
  // of "Under Review" into its real bucket (DemandArc Accepted/Rejected/Client
  // Accepted) everywhere that reads raw status — Programs/Components rollups
  // included, which group by status, not qa_status. Leads still at Needs
  // Review or with no qa_status yet are left alone (nothing to accept).
  const handleAcceptSelectedQaStatuses = async () => {
    if (selected.size === 0) return;
    setAcceptingQaStatuses(true);
    setBulkMsg(null);
    try {
      const res = await axios.post(`${API}/leads/accept-qa-statuses`, { lead_ids: [...selected] });
      setBulkMsg({
        ok: true,
        text: `${res.data.updated} lead${res.data.updated !== 1 ? 's' : ''} accepted.` +
          (res.data.skipped ? ` ${res.data.skipped} skipped (still Needs Review or no QA result yet).` : ''),
      });
      setSelected(new Set());
      reloadLeadsPreservingScroll();
    } catch (e) {
      setBulkMsg({ ok: false, text: e.response?.data?.detail || 'Accept failed' });
    }
    setAcceptingQaStatuses(false);
  };

  // ---------------------------------------------------------------------------
  // Rerun QA
  // ---------------------------------------------------------------------------
  const handleRerunQA = async (onlyNull) => {
    if (!level || !entityId) return;
    setRerunning(onlyNull ? 'backfill' : 'all');
    setRerunMsg(null);
    try {
      const res = await axios.post(`${API}/leads/rerun-qa`, null, {
        params: { [apiParam]: entityId, only_null: onlyNull },
      });
      const label = onlyNull ? 'Backfill' : 'Re-run all';
      setRerunMsg({ ok: true, text: `${label} complete — ${res.data.updated} lead${res.data.updated !== 1 ? 's' : ''} updated.${res.data.errors?.length ? ` ${res.data.errors.length} error(s).` : ''}` });
      reloadLeadsPreservingScroll();
    } catch (e) {
      setRerunMsg({ ok: false, text: e.response?.data?.detail || 'Rerun failed' });
    }
    setRerunning(false);
  };

  const handleRerunSelected = async () => {
    if (selected.size === 0) return;
    setRerunning('selected');
    setRerunMsg(null);
    try {
      const res = await axios.post(`${API}/leads/bulk-rerun-qa`, { lead_ids: [...selected] });
      setRerunMsg({ ok: true, text: `Re-run complete — ${res.data.updated} lead${res.data.updated !== 1 ? 's' : ''} updated${res.data.skipped_overrides ? `, ${res.data.skipped_overrides} skipped (override set)` : ''}.${res.data.errors?.length ? ` ${res.data.errors.length} error(s).` : ''}` });
      setSelected(new Set());
      reloadLeadsPreservingScroll();
    } catch (e) {
      setRerunMsg({ ok: false, text: e.response?.data?.detail || 'Rerun failed' });
    }
    setRerunning(false);
  };

  // ---------------------------------------------------------------------------
  // Export handlers — Full List (F-J) / Accepted (F-G). Rejected (F-H)/Returned
  // (F-I) buttons removed 2026-08-11 — see handleExportFilteredView() below for
  // the "export this view" feature added the same round.
  // Each export type can have its own saved default config, assigned in
  // ExportConfigPanel (exportDefaults[type]); falls back to legacy behavior
  // (all fields, NetRA labels) when a type has no default assigned.
  // ---------------------------------------------------------------------------

  // scopeOverride {level, entityId}, when given, replaces the page-level
  // program/component/goal scope for just this one call — e.g. exporting one
  // specific goal's Accepted list while the table itself is still showing
  // the whole program.
  const buildExportParams = (exportType, extraParams, scopeOverride) => {
    const scopeLevel = scopeOverride?.level || level;
    const scopeEntityId = scopeOverride ? scopeOverride.entityId : entityId;
    const params = new URLSearchParams({ [scopeLevel + '_id']: scopeEntityId });
    const cfg = exportDefaults[exportType];
    if (cfg?.id) params.set('config_id', cfg.id);
    if (extraParams) {
      Object.entries(extraParams).forEach(([k, v]) => params.set(k, v));
    }
    return params;
  };

  const EXPORT_MIME = {
    csv: 'text/csv',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };

  const downloadExport = async (exportType, endpoint, filenamePrefix, setBusy, extraParams, format = 'csv', scopeOverride) => {
    if (!entityId && !scopeOverride?.entityId) return;
    setBusy(true);
    try {
      const params = buildExportParams(exportType, { ...(extraParams || {}), format }, scopeOverride);
      const res = await axios.get(`${API}/leads/${endpoint}?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: EXPORT_MIME[format] }));
      const a = document.createElement('a'); a.href = url;
      const scopeLabel = scopeOverride ? `${scopeOverride.level}${scopeOverride.entityId}` : `${level}${entityId}`;
      a.download = `${filenamePrefix}_${scopeLabel}.${format}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed: ' + (e.response?.data?.detail || e.message || 'unknown error')); }
    setBusy(false);
  };

  const handleExportFull = (format = 'csv') => {
    downloadExport('full', 'export-full', 'export_full', setExportingFull, null, format);
    setShowFullMenu(false);
  };
  const handleExportAccepted = () => {
    if (!acceptedIncludeDemandArc && !acceptedIncludeClient) return;
    const scopeOverride = acceptedGoalId
      ? { level: 'goal', entityId: acceptedGoalId }
      : acceptedComponentId
      ? { level: 'component', entityId: acceptedComponentId }
      : null;
    downloadExport('accepted', 'export-accepted', 'export_accepted', setExportingAccepted, {
      demandarc: acceptedIncludeDemandArc,
      client_accepted: acceptedIncludeClient,
      promoted_only: acceptedPromotedOnly,
    }, acceptedFormat, scopeOverride);
    setShowAcceptedMenu(false);
  };
  // Filtered-view export — everything currently visible in the table exactly as
  // shown: filteredLeads (current tab/delivery/column-filter/sort applied) and
  // orderedVisibleCols (current column set + order), built client-side since
  // there's no server-side equivalent of "whatever this PM has filtered/toggled
  // right now". Used to fall back to force-adding lead_id (NeTrA Lead ID) if it
  // wasn't already in orderedVisibleCols — removed 2026-09-11, since that
  // branch was actually dead code (lead_id is one of ALL_COLUMNS' fixed
  // `always: true` columns — Lead ID/Name/Email/Company/QA Status/Flags — so
  // it can never be absent from orderedVisibleCols in the first place; this
  // page's own column picker never even lists it as toggleable). That
  // `always: true` set is a deliberate, separate design choice for this
  // internal QA working table (a PM needs to reference a specific lead's own
  // id while reviewing) and is unaffected by the "no NetraID on a client
  // export by default" ask this round — this export naturally still includes
  // it, same as the on-screen table always does.
  const csvCell = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleExportFilteredView = () => {
    if (!filteredLeads.length) return;
    const cols = orderedVisibleCols;
    const rows = [
      cols.map(c => c.label),
      ...filteredLeads.map(lead => cols.map(c => getColValue(lead, c.key))),
    ];
    const csvText = rows.map(row => row.map(csvCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csvText], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_filtered_view_${level}${entityId}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------------------------------------------------------------------------
  // Convertr sync
  // ---------------------------------------------------------------------------
  const handleConvertrSync = async () => {
    if (!convertrFile || !selectedProgram) return;
    setConvertrSyncing(true);
    setConvertrResult(null);
    try {
      const form = new FormData();
      form.append('file', convertrFile);
      form.append('program_id', selectedProgram);
      const res = await axios.post(`${API}/leads/convertr-sync`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setConvertrResult({ ok: true, data: res.data });
      reloadLeadsPreservingScroll();
    } catch (e) {
      setConvertrResult({ ok: false, text: e.response?.data?.detail || 'Sync failed' });
    }
    setConvertrSyncing(false);
  };

  // ---------------------------------------------------------------------------
  // QA status counts for tabs
  // ---------------------------------------------------------------------------
  // Ordered, visible column objects — drives all header + cell rendering.
  // Any key in visibleCols that isn't yet in colOrder (e.g. a required-field
  // column just defaulted on for this program, or a stale saved order from
  // before that field was tracked) is appended at the end rather than
  // silently dropped — colOrder only ever reorders what's already there.
  let orderedVisibleCols = (colOrder.length > 0
    ? [...colOrder.filter(k => visibleCols.has(k)), ...[...visibleCols].filter(k => !colOrder.includes(k))]
    : computeSmartOrder(visibleCols, leads)
  ).map(k => ALL_COLUMNS.find(c => c.key === k)).filter(Boolean);

  // "Client Del #" shown by default on the Under Review tab so a PM can sort
  // by batch number — without touching the PM's own visibleCols/colOrder
  // choices (and their localStorage persistence), so it reverts to their
  // normal column picker selection on every other tab.
  if (qaFilter === 'Under Review' && !orderedVisibleCols.some(c => c.key === 'client_delivery_number')) {
    const clientDelCol = ALL_COLUMNS.find(c => c.key === 'client_delivery_number');
    if (clientDelCol) orderedVisibleCols = [...orderedVisibleCols, clientDelCol];
  }

  // Lead.status alone, matching goals.py's rollup exactly — Needs Review /
  // Not Run / Ready / Client Returned are per-lead badges *within* "Under
  // Review" now (see QABadge / src/utils/leadStatus.js), not separate tabs.
  // There's no "Pending Review" tab here on purpose — GET /leads/ never
  // returns Pending Review leads at all (see backend CLAUDE.md); a lead only
  // becomes visible in Lead QA Review once Accept has moved it to Under
  // Review.
  const counts = {
    all:                              leads.length,
    'Under Review':                   leads.filter(l => l.status === 'Under Review').length,
    'Approved Pending Call Review':   leads.filter(l => l.status === 'Approved Pending Call Review').length,
    'DemandArc Accepted':             leads.filter(l => l.status === 'DemandArc Accepted').length,
    'Client Accepted':                leads.filter(l => l.status === 'Client Accepted').length,
    'Rejected':                       leads.filter(l => l.status === 'Rejected').length,
  };

  // "Approved Pending Call Review" only shows up as a tab when the current
  // selection needs it — see includesCallReview above.
  const tabs = [
    { key: 'all',                  label: 'All' },
    { key: 'Under Review',         label: 'Under Review' },
    ...(includesCallReview ? [{ key: 'Approved Pending Call Review', label: 'Approved Pending Call Review' }] : []),
    { key: 'DemandArc Accepted',   label: 'DemandArc Accepted' },
    { key: 'Client Accepted',      label: '✓ Client Accepted' },
    { key: 'Rejected',             label: 'Rejected' },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      {showQueue && (
        <QAPriorityQueue canOverride={canOverrideQueue(currentUser?.role)} onClose={() => setShowQueue(false)}
          onJump={({ programId, status }) => {
            // Same state the program picker and the status tabs set; the popup only picks where to land.
            setSelectedProgram(String(programId)); setSelectedComponent(''); setSelectedGoal('');
            setQaFilter(status && VALID_STATUS_PARAMS.has(status) ? status : 'all');
            setShowQueue(false);
          }} />
      )}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>🔍 Lead QA Review</h2>
          <button type="button" onClick={() => setShowQueue(true)}
            style={{ background: 'none', border: 'none', padding: 0, color: '#2e6bb5', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            View QA Priority Queue
          </button>
        </div>
        <div style={{ fontSize: '13px', color: '#777', marginTop: '4px' }}>
          Review automated QA results, apply PM overrides, and backfill QA on older leads.
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
        <SearchableSelect
          label="Program"
          value={selectedProgram}
          onChange={setSelectedProgram}
          options={programOptionGroups.options}
          closedOptions={programOptionGroups.closedOptions}
          placeholder="— select program —"
        />
        <SearchableSelect
          label="Component"
          value={selectedComponent}
          onChange={setSelectedComponent}
          options={componentOptionGroups.options}
          closedOptions={componentOptionGroups.closedOptions}
          placeholder="— select component —"
        />
        <SearchableSelect
          label="Goal"
          value={selectedGoal}
          onChange={setSelectedGoal}
          options={goalOptionGroups.options}
          closedOptions={goalOptionGroups.closedOptions}
          placeholder="— select goal —"
        />
        {(selectedProgram || selectedComponent || selectedGoal) && (
          <div style={{ paddingBottom: '2px' }}>
            <div style={labelStyle}>&nbsp;</div>
            <button onClick={clearSelections} style={{
              padding: '6px 14px', border: '1px solid #ccc', borderRadius: '4px',
              background: '#fff', color: '#666', cursor: 'pointer', fontSize: '13px',
            }}>✕ Clear</button>
          </div>
        )}
        {entityId && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', paddingBottom: '2px' }}>
            <div style={labelStyle}>&nbsp;</div>
            <button onClick={() => handleRerunQA(true)} disabled={!!rerunning} style={btnStyle('#7d3c98', !!rerunning)}>
              {rerunning === 'backfill' ? 'Running…' : '▶ Backfill QA'}
            </button>
            <button onClick={() => handleRerunQA(false)} disabled={!!rerunning} style={btnStyle('#1a6da8', !!rerunning)}>
              {rerunning === 'all' ? 'Running…' : '↺ Re-run All'}
            </button>
            <span style={{ width: '1px', height: '20px', background: '#ddd', display: 'inline-block', margin: '0 4px' }} />
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowFullMenu(v => !v)}
                disabled={exportingFull}
                style={btnStyle('#2c3e50', exportingFull)}
                title={exportDefaults.full?.name || 'All fields, NetRA labels'}
              >
                {exportingFull ? 'Exporting…' : '⬇ Full List ▾'}
              </button>
              {showFullMenu && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 20,
                  background: '#fff', border: '1px solid #ccc', borderRadius: '6px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.12)', padding: '6px', width: '160px',
                }}>
                  <button
                    onClick={() => handleExportFull('csv')}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', borderRadius: '4px' }}
                  >Export as CSV</button>
                  <button
                    onClick={() => handleExportFull('xlsx')}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', borderRadius: '4px' }}
                  >Export as XLSX</button>
                </div>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowAcceptedMenu(v => !v)}
                disabled={exportingAccepted}
                style={btnStyle('#1a7a4a', exportingAccepted)}
                title={exportDefaults.accepted?.name || 'All fields, NetRA labels'}
              >
                {exportingAccepted ? 'Exporting…' : '⬇ Accepted ▾'}
              </button>
              {showAcceptedMenu && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 20,
                  background: '#fff', border: '1px solid #ccc', borderRadius: '6px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.12)', padding: '12px', width: '220px',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>
                    Include in export:
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', marginBottom: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={acceptedIncludeDemandArc}
                      onChange={e => setAcceptedIncludeDemandArc(e.target.checked)}
                    />
                    DemandArc Accepted
                  </label>
                  <label
                    style={{
                      display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', marginLeft: '20px',
                      marginBottom: '8px', cursor: acceptedIncludeDemandArc ? 'pointer' : 'default',
                      color: acceptedIncludeDemandArc ? '#333' : '#aaa',
                    }}
                    title="By default this also includes leads still awaiting call review, and 'Ready' leads that passed QA but were never promoted — both can make this download larger than the dashboard's own DemandArc Accepted count. Check this to match that count exactly."
                  >
                    <input
                      type="checkbox"
                      disabled={!acceptedIncludeDemandArc}
                      checked={acceptedPromotedOnly}
                      onChange={e => setAcceptedPromotedOnly(e.target.checked)}
                    />
                    Match dashboard count exactly
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', marginBottom: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={acceptedIncludeClient}
                      onChange={e => setAcceptedIncludeClient(e.target.checked)}
                    />
                    ✓ Client Accepted
                  </label>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                    Format:
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer' }}>
                      <input type="radio" name="acceptedFormat" checked={acceptedFormat === 'csv'} onChange={() => setAcceptedFormat('csv')} />
                      CSV
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer' }}>
                      <input type="radio" name="acceptedFormat" checked={acceptedFormat === 'xlsx'} onChange={() => setAcceptedFormat('xlsx')} />
                      XLSX
                    </label>
                  </div>
                  {components.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                        Component (optional):
                      </div>
                      <select
                        value={acceptedComponentId}
                        onChange={e => { setAcceptedComponentId(e.target.value); if (e.target.value) setAcceptedGoalId(''); }}
                        style={{ width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px' }}
                        title="Scopes just this download to one component. Leave as 'All' to export the current view as normal."
                      >
                        <option value="">All (current view)</option>
                        {components.map(c => (
                          <option key={c.id} value={c.id}>{c.component_name || `Component ${c.id}`}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {goals.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                        Goal (optional):
                      </div>
                      <select
                        value={acceptedGoalId}
                        onChange={e => { setAcceptedGoalId(e.target.value); if (e.target.value) setAcceptedComponentId(''); }}
                        style={{ width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px' }}
                        title="Scopes just this download to one goal — e.g. for a program like SailPoint where each goal comes from a different upload source. Leave as 'All' to export the current view as normal."
                      >
                        <option value="">All (current view)</option>
                        {goals.map(g => (
                          <option key={g.id} value={g.id}>{g.goal_name || `Goal ${g.id}`}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {!acceptedIncludeDemandArc && !acceptedIncludeClient && (
                    <div style={{ fontSize: '11px', color: '#c0392b', marginBottom: '8px' }}>Select at least one.</div>
                  )}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleExportAccepted}
                      disabled={!acceptedIncludeDemandArc && !acceptedIncludeClient}
                      style={{
                        ...btnStyle('#1a7a4a', !acceptedIncludeDemandArc && !acceptedIncludeClient),
                        flex: 1, padding: '6px 10px',
                      }}
                    >
                      ⬇ Download
                    </button>
                    <button
                      onClick={() => setShowAcceptedMenu(false)}
                      style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', color: '#666', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee', textAlign: 'center' }}>
                    <span
                      onClick={() => {
                        if (!resolvedProgramId) return;
                        window.open(`/client-delivery-wizard?program_id=${resolvedProgramId}`, '_blank');
                        setShowAcceptedMenu(false);
                      }}
                      style={{
                        fontSize: '12px', color: resolvedProgramId ? '#1a6da8' : '#aaa',
                        cursor: resolvedProgramId ? 'pointer' : 'default', textDecoration: 'underline',
                      }}
                      title={resolvedProgramId ? undefined : 'Select a program, component, or goal first'}
                    >
                      Or use the Client Delivery Wizard →
                    </span>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={handleExportFilteredView}
              disabled={!filteredLeads.length}
              style={btnStyle('#8e44ad', !filteredLeads.length)}
              title="Export exactly what's shown below right now — current tab/filters/sort, current visible columns (always includes NeTRA ID)"
            >
              ⬇ Export This View
            </button>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowExportPanel(v => !v)}
                style={{ ...btnStyle('#7f8c8d', false), padding: '6px 10px' }}
                title="Configure export fields, column names & per-type defaults"
              >⚙ Export Settings</button>
              {showExportPanel && (
                <ExportConfigPanel
                  key={resolvedProgramId}
                  programId={resolvedProgramId}
                  fields={exportFields}
                  defaults={exportDefaults}
                  onDefaultsChange={setExportDefaults}
                  onClose={() => setShowExportPanel(false)}
                />
              )}
            </div>
          </div>
        )}

        {/* Convertr Sync — only shown when a program is selected */}
        {selectedProgram && (
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={() => { setConvertrSyncOpen(o => !o); setConvertrResult(null); }}
              style={{
                padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
                background: convertrSyncOpen ? '#1a5276' : '#2c3e50',
                color: '#fff', border: 'none',
              }}
            >
              {convertrSyncOpen ? '▲ Convertr Sync' : '🔄 Convertr Sync'}
            </button>

            {convertrSyncOpen && (
              <div style={{
                marginTop: '8px', padding: '14px 16px', borderRadius: '8px',
                border: '1px solid #aed6f1', background: '#eaf4fb',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#1a5276' }}>
                  Sync Convertr-processed CSV
                </div>
                <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
                  Upload a Convertr export CSV. Leads will be matched by <strong>email</strong> within
                  the selected program and their Convertr status fields will be updated.
                </div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={e => { setConvertrFile(e.target.files[0] || null); setConvertrResult(null); }}
                  style={{ fontSize: '13px', marginBottom: '8px', display: 'block' }}
                />
                <button
                  onClick={handleConvertrSync}
                  disabled={!convertrFile || convertrSyncing}
                  style={{
                    padding: '6px 16px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
                    background: (!convertrFile || convertrSyncing) ? '#aaa' : '#1a5276',
                    color: '#fff', border: 'none',
                  }}
                >
                  {convertrSyncing ? 'Syncing…' : 'Upload & Sync'}
                </button>

                {convertrResult && convertrResult.ok && (
                  <div style={{
                    marginTop: '10px', padding: '10px 14px', borderRadius: '6px',
                    background: '#eafaf1', border: '1px solid #a9dfbf', fontSize: '13px', color: '#1e8449',
                  }}>
                    ✓ Sync complete — <strong>{convertrResult.data.matched}</strong> matched,{' '}
                    <strong>{convertrResult.data.updated}</strong> updated,{' '}
                    <strong>{convertrResult.data.unmatched}</strong> unmatched
                    {convertrResult.data.skipped_blank_email > 0 && (
                      <span style={{ color: '#888' }}>, {convertrResult.data.skipped_blank_email} skipped (no email)</span>
                    )}
                    {convertrResult.data.unmatched > 0 && (
                      <details style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>
                        <summary style={{ cursor: 'pointer' }}>
                          {convertrResult.data.unmatched_emails.length} unmatched email{convertrResult.data.unmatched !== 1 ? 's' : ''}
                          {convertrResult.data.unmatched > convertrResult.data.unmatched_emails.length ? ` (showing first ${convertrResult.data.unmatched_emails.length})` : ''}
                        </summary>
                        <div style={{ marginTop: '4px', lineHeight: '1.6' }}>
                          {convertrResult.data.unmatched_emails.map(e => <div key={e}>{e}</div>)}
                        </div>
                      </details>
                    )}
                  </div>
                )}
                {convertrResult && !convertrResult.ok && (
                  <div style={{
                    marginTop: '10px', padding: '10px 14px', borderRadius: '6px',
                    background: '#fdedec', border: '1px solid #f5b7b1', fontSize: '13px', color: '#c0392b',
                  }}>
                    ✗ {convertrResult.text}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {pendingDeliveries.length > 0 && (
        <div style={{
          marginBottom: '14px', padding: '10px 14px', borderRadius: '6px', fontSize: '13px',
          background: '#eef6fc', border: '1px solid #b8dff5', color: '#1a5276',
          display: 'flex', flexDirection: 'column', gap: '8px',
        }}>
          {pendingDeliveries.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <span>
                📥 New leads pending: <strong>{d.filename || `Delivery #${d.id}`}</strong> — accept and start QA?
              </span>
              <button
                onClick={() => handleAcceptPendingDelivery(d.id)}
                disabled={acceptingDeliveryId === d.id}
                style={{
                  padding: '5px 14px', borderRadius: '4px', border: 'none', fontSize: '12px', fontWeight: '600',
                  background: acceptingDeliveryId === d.id ? '#a9cce3' : '#2980b9', color: '#fff',
                  cursor: acceptingDeliveryId === d.id ? 'default' : 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {acceptingDeliveryId === d.id ? 'Accepting…' : 'Accept & Start QA'}
              </button>
            </div>
          ))}
        </div>
      )}

      {rerunMsg && (
        <div style={{
          marginBottom: '14px', padding: '10px 14px', borderRadius: '6px', fontSize: '13px',
          background: rerunMsg.ok ? '#eafaf1' : '#fdedec',
          color: rerunMsg.ok ? '#1e8449' : '#c0392b',
          border: `1px solid ${rerunMsg.ok ? '#a9dfbf' : '#f5b7b1'}`,
        }}>
          {rerunMsg.text}
        </div>
      )}

      {/* Title split summary banner */}
      {/* ── Component / Goal rollup ── */}
      {rollupGoals.length > 0 && (() => {
        const isProgram = level === 'program';
        const rows = goalsToStatusBreakdownRows(rollupGoals, isProgram, allComponents);
        const rejectedTitle = (rClient, rDa) => `Rejected - by Client: ${rClient} · Rejected - by DA: ${rDa}`;
        const overCapTitle = (overCapGoals) => overCapGoals
          .map(g => `${g.goalName}: ${g.accepted}/${g.ordered} accepted (+${g.accepted - g.ordered} over cap)`)
          .join('\n');

        // Every top-level row's key, so the expand chevron (added below, only
        // for the program-level rollup) can tell a real Component row apart
        // from a Goal row rendered underneath it via `subRows` — both go
        // through this same extraColumns set, but only a Component expands.
        const topLevelKeys = new Set(rows.map(r => r.key));

        // Shared by both the top-level rollup and its expanded Goal sub-rows
        // (rendered inline via StatusBreakdownTable's `subRows`, not a
        // second nested table), so the two can't drift apart — same "one
        // implementation" reasoning as StatusBreakdownTable itself.
        const rollupExtraColumns = [
          ...(isProgram ? [{
            key: 'expand', header: '', insertAfter: 'label',
            render: r => (topLevelKeys.has(r.key) ? (
              <span style={{ color: '#888', fontSize: '11px' }}>{expandedRollupRows[r.key] ? '▼' : '▶'}</span>
            ) : null),
          }] : []),
          {
            key: 'capWarning', header: '', insertAfter: 'label',
            render: r => (r.overCapGoals?.length > 0 ? (
              <span title={overCapTitle(r.overCapGoals)} style={{ color: '#e67e22', cursor: 'help', fontSize: '13px' }}>⚠</span>
            ) : null),
          },
          {
            key: 'qty', header: 'Qty Ordered', insertAfter: 'label',
            render: r => r.qty || '—',
            renderTotal: rows => { const t = rows.reduce((s, r) => s + r.qty, 0); return t || '—'; },
          },
          {
            key: 'pct', header: '% Complete', insertAfter: 'DemandArc Accepted',
            render: r => {
              const pct = r.qty > 0 ? Math.round((r.counts['Client Accepted'] / r.qty) * 100) : null;
              return <span style={{ color: pct >= 100 ? '#1e8449' : '#555', fontWeight: '500' }}>{pct != null ? pct + '%' : '—'}</span>;
            },
            renderTotal: rows => {
              const qty = rows.reduce((s, r) => s + r.qty, 0);
              const client = rows.reduce((s, r) => s + r.counts['Client Accepted'], 0);
              const pct = qty > 0 ? Math.round((client / qty) * 100) : null;
              return <span style={{ color: pct >= 100 ? '#1e8449' : '#555' }}>{pct != null ? pct + '%' : '—'}</span>;
            },
          },
          {
            key: 'needed', header: 'Needed',
            render: r => {
              const needed = r.qty > 0 ? Math.max(0, r.qty - r.counts['Client Accepted']) : null;
              return <span style={{ color: needed === 0 ? '#1e8449' : '#e67e22', fontWeight: needed === 0 ? '600' : 'normal' }}>{needed != null ? needed : '—'}</span>;
            },
            renderTotal: rows => {
              const qty = rows.reduce((s, r) => s + r.qty, 0);
              const client = rows.reduce((s, r) => s + r.counts['Client Accepted'], 0);
              const needed = qty > 0 ? Math.max(0, qty - client) : null;
              return <span style={{ color: needed === 0 ? '#1e8449' : '#e67e22' }}>{needed != null ? needed : '—'}</span>;
            },
          },
        ];
        const rejectedStatusCell = (status, count, r) => {
          if (status !== 'Rejected') return undefined;
          return (
            <td key={status} style={{ ...rollupTdStyle, color: count > 0 ? '#c0392b' : '#888', cursor: count > 0 ? 'help' : 'default' }}
              title={count > 0 ? rejectedTitle(r.rejectedClient, r.rejectedDa) : undefined}>
              {count}
            </td>
          );
        };
        const rejectedStatusTotalCell = (status, total, rows) => {
          if (status !== 'Rejected') return undefined;
          const rClient = rows.reduce((s, r) => s + r.rejectedClient, 0);
          const rDa = rows.reduce((s, r) => s + r.rejectedDa, 0);
          return (
            <td key={status} style={{ ...rollupTdStyle, color: total > 0 ? '#c0392b' : '#888', cursor: total > 0 ? 'help' : 'default' }}
              title={total > 0 ? rejectedTitle(rClient, rDa) : undefined}>
              {total}
            </td>
          );
        };

        return (
          <StatusBreakdownTable
            rows={rows}
            includesCallReview={includesCallReview}
            title={isProgram ? 'COMPONENT ROLLUP' : 'GOAL ROLLUP'}
            onRefresh={reloadLeadsPreservingScroll}
            extraColumns={rollupExtraColumns}
            renderStatusCell={rejectedStatusCell}
            renderStatusTotalCell={rejectedStatusTotalCell}
            onRowClick={isProgram ? (r) => setExpandedRollupRows(prev => ({ ...prev, [r.key]: !prev[r.key] })) : undefined}
            subRows={isProgram ? (r) => {
              if (!expandedRollupRows[r.key]) return null;
              return goalsToStatusBreakdownRows(
                rollupGoals.filter(g => String(g.component_id) === r.key),
                false,
                allComponents,
              );
            } : undefined}
          />
        );
      })()}

      {!entityId ? (
        <div style={{ color: '#aaa', fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>
          Select a program, component, or goal above to view leads.
        </div>
      ) : loading ? (
        <div style={{ color: '#aaa', fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>
          Loading…
        </div>
      ) : leads.length === 0 ? (
        <div style={{ color: '#aaa', fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>
          No leads found for this {level ? LEVEL_LABELS[level].toLowerCase() : 'selection'}.
        </div>
      ) : (
        <>
          {/* Status tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid #eaf2fb', paddingBottom: '0' }}>
            {tabs.map(t => {
              const active = qaFilter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => { setQaFilter(t.key); setSelected(new Set()); }}
                  style={{
                    padding: '6px 14px', border: 'none', borderBottom: active ? '2px solid #1a6da8' : '2px solid transparent',
                    background: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: active ? '700' : '400',
                    color: active ? '#1a6da8' : '#666', marginBottom: '-2px',
                  }}
                >
                  {t.label}
                  <span style={{
                    marginLeft: '5px', background: active ? '#1a6da8' : '#e0e0e0',
                    color: active ? 'white' : '#666', borderRadius: '10px',
                    padding: '1px 7px', fontSize: '10px', fontWeight: '600',
                  }}>
                    {counts[t.key]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Delivery picker — jump straight to one batch on any tab, defaults to
              "All" so nothing is hidden until a PM explicitly narrows it. Shown
              whenever this tab has delivery-sourced leads (and always on Under
              Review, as before). */}
          {(qaFilter === 'Under Review' || deliveryOptions.length > 0) && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Delivery:</label>
              <select
                value={String(activeDeliverySelection)}
                onChange={e => {
                  const v = e.target.value;
                  setDeliverySelection(v === 'all' ? 'all' : Number(v));
                  setSelected(new Set());
                }}
                style={{ ...inputStyle, width: 'auto' }}
              >
                <option value="all">All ({deliveryPoolLeads.length})</option>
                {deliveryOptions.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.label}{d.date ? ` — ${d.date}` : ''} ({d.count})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div style={{
              display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap',
              background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '6px',
              padding: '12px 16px', marginBottom: '12px',
            }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#1a6da8', alignSelf: 'center' }}>
                {selected.size} lead{selected.size !== 1 ? 's' : ''} selected
              </div>
              <div>
                <label style={labelStyle}>Override</label>
                <select
                  value={bulkOverride}
                  onChange={e => setBulkOverride(e.target.value)}
                  style={{ ...inputStyle, width: 'auto' }}
                >
                  <option value="">— Choose —</option>
                  <option value="Client Accepted">✓ Client Accepted</option>
                  <option value="Client Returned">↩ Client Returned – Replacement Needed</option>
                  <option value="Needs Review">⚑ Needs Review – Revisit Later</option>
                  <option value="Accept">PM Accept</option>
                  <option value="Reject">Rejected - by DA</option>
                  <option value="Client Rejected">Rejected - by Client</option>
                  {includesCallReview && <option value="Approved Pending Call Review">📞 Approved Pending Call Review</option>}
                </select>
              </div>
              {(qaFilter === 'Under Review' || qaFilter === 'Approved Pending Call Review') && (
                <button
                  onClick={handleAcceptSelectedQaStatuses}
                  disabled={acceptingQaStatuses}
                  title={qaFilter === 'Approved Pending Call Review'
                    ? 'Finalizes each selected lead into DemandArc Accepted now that its call review is complete'
                    : "Promotes each selected lead's own computed QA status (Accepted/Rejected/Client Accepted) into its permanent status, moving it out of Under Review — or into Approved Pending Call Review first, if this program requires it"}
                  style={btnStyle('#1e8449', acceptingQaStatuses)}
                >
                  {acceptingQaStatuses ? 'Accepting…' : '✓ Accept Selected QA Statuses'}
                </button>
              )}
              <div style={{ flex: 1, minWidth: '180px' }}>
                <label style={labelStyle}>Note (optional)</label>
                <input
                  type="text"
                  placeholder="Reason…"
                  value={bulkNote}
                  onChange={e => setBulkNote(e.target.value)}
                  disabled={bulkClearNote}
                  style={inputStyle}
                />
                <label style={{ fontSize: '11px', color: '#888', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                  <input
                    type="checkbox"
                    checked={bulkClearNote}
                    onChange={e => setBulkClearNote(e.target.checked)}
                  />
                  ✕ Clear existing notes on selected leads
                </label>
              </div>
              <button
                onClick={handleBulkOverride}
                disabled={bulkSaving || !bulkOverride}
                style={btnStyle('#2471a3', bulkSaving || !bulkOverride)}
              >
                {bulkSaving ? 'Saving…' : 'Apply to Selected'}
              </button>
              <button
                onClick={handleRerunSelected}
                disabled={!!rerunning}
                style={btnStyle('#2471a3', !!rerunning)}
              >
                {rerunning === 'selected' ? 'Running…' : '↺ Re-run QA'}
              </button>
              <button
                onClick={() => setSelected(new Set())}
                style={{ ...btnStyle('#95a5a6', false), background: 'none', color: '#888', border: '1px solid #ccc' }}
              >
                Clear
              </button>
              {bulkMsg && (
                <div style={{ fontSize: '12px', color: bulkMsg.ok ? '#1e8449' : '#c0392b', alignSelf: 'center' }}>
                  {bulkMsg.text}
                </div>
              )}
            </div>
          )}

          {/* Column picker + table */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px' }}>
              Notes
              <select
                value={noteFilter}
                onChange={e => setNoteFilter(e.target.value)}
                title="Show or hide leads by who left the review note"
                style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', background: noteFilter === 'any' ? '#fff' : '#fef9e7' }}
              >
                {NOTE_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <button
              onClick={() => setColPickerOpen(true)}
              style={{ padding: '5px 12px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#555' }}
            >
              ⚙ Columns
            </button>
          </div>

          {colPickerOpen && (
            <div
              onClick={() => setColPickerOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  background: '#fff', border: '1px solid #ccc', borderRadius: '8px',
                  width: 'min(720px, 96vw)', maxHeight: '90vh', overflowY: 'auto',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.25)', fontSize: '12px', padding: '18px 22px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '14px' }}>
                  <div style={{ fontWeight: 700, fontSize: '16px' }}>Column Settings</div>
                  <button onClick={() => setColPickerOpen(false)} style={{ fontSize: '18px', padding: '3px 8px', cursor: 'pointer', border: 'none', background: 'none', color: '#888' }}>✕</button>
                </div>

                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {/* Left: visible columns, reorderable */}
                  <div style={{ flex: '1 1 280px', minWidth: '260px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Visible Columns (drag to reorder)
                    </div>
                    <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
                      {orderedVisibleCols.map(col => (
                        <div
                          key={col.key}
                          draggable={!col.always}
                          onDragStart={() => { dragColRef.current = col.key; }}
                          onDragOver={e => e.preventDefault()}
                          onDrop={() => reorderCols(dragColRef.current, col.key)}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #f5f5f5', cursor: col.always ? 'default' : 'grab' }}
                        >
                          <span>
                            {col.always ? '' : '⠿ '}{colLabel(col)}
                            {col.always && <span style={{ color: '#aaa', fontSize: '10px', marginLeft: '6px' }}>(always shown)</span>}
                          </span>
                          {!col.always && (
                            <button onClick={() => toggleCol(col.key)} style={{ fontSize: '10px', color: '#b03a2e', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: '10px' }}>
                      <button onClick={() => {
                        setColOrder(computeSmartOrder(visibleCols, leads));
                        if (entityId) localStorage.removeItem(`nqa_order_${entityId}`);
                        if (entityId) localStorage.removeItem(`nqa_widths_${entityId}`);
                        setColWidths({});
                      }} style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid #ccc', borderRadius: '3px', background: '#fff', cursor: 'pointer', color: '#555' }}>
                        ↺ Reset column order
                      </button>
                    </div>
                  </div>

                  {/* Right: add/remove columns + saved views */}
                  <div style={{ flex: '1 1 280px', minWidth: '260px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Show / Hide Columns
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', padding: '4px 8px' }}>
                      {ALL_COLUMNS.filter(c => !c.always).map(col => {
                        const aliases = exportFields.find(f => f.key === col.key)?.aliases || [];
                        return (
                          <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0', cursor: 'pointer', fontSize: '13px', color: '#333' }}>
                            <input type="checkbox" checked={visibleCols.has(col.key)} onChange={() => toggleCol(col.key)} />
                            {colLabel(col)}
                            {aliases.length > 0 && (
                              <span
                                title={`Upload files can also use these column headers for this field: ${aliases.join(', ')}`}
                                style={{ color: '#8ba7c4', fontSize: '11px', cursor: 'help' }}
                              >
                                ⓘ
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Saved Column Views
                      </div>
                      {savedColumnPresets.length === 0 && (
                        <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '6px' }}>No saved views yet.</div>
                      )}
                      {savedColumnPresets.map(p => (
                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', fontSize: '12px' }}>
                          <button onClick={() => loadColumnPreset(p)} disabled={presetBusy} style={{ background: 'none', border: 'none', color: '#1a6da8', cursor: 'pointer', textAlign: 'left', padding: 0, fontSize: '12px' }}>
                            {p.name}
                          </button>
                          <button onClick={() => deleteColumnPreset(p.id)} disabled={presetBusy} style={{ fontSize: '10px', color: '#b03a2e', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                        <input
                          value={newPresetName}
                          onChange={e => setNewPresetName(e.target.value)}
                          placeholder="Name this view…"
                          style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '3px' }}
                        />
                        <button disabled={presetBusy || !newPresetName.trim()} onClick={saveCurrentAsPreset} style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid #ccc', borderRadius: '3px', background: '#fff', cursor: 'pointer' }}>
                          Save
                        </button>
                      </div>
                      {presetError && <div style={{ color: '#b03a2e', fontSize: '10px', marginTop: '4px' }}>{presetError}</div>}
                      <div style={{ fontSize: '10px', color: '#aaa', marginTop: '6px' }}>Saved views are shared with the whole team.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {filteredLeads.length === 0 ? (
            <div style={{ fontSize: '13px', padding: '32px 0', textAlign: 'center', color: '#888' }}>
              <div style={{ marginBottom: '8px' }}>No leads match the current filters.</div>
              {filtersActive && (
                <button onClick={clearAllFilters} style={{ fontSize: '12px', padding: '5px 14px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#555' }}>
                  ✕ Clear filters
                </button>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid #d6eaf8', borderRadius: '8px' }}>
              <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: 36 + orderedVisibleCols.reduce((s, c) => s + (colWidths[c.key] || COL_DEFAULT_WIDTHS[c.key] || 120), 0) }}>
                <colgroup>
                  <col style={{ width: 36 }} />
                  {orderedVisibleCols.map(col => (
                    <col key={col.key} style={{ width: colWidths[col.key] || COL_DEFAULT_WIDTHS[col.key] || 120 }} />
                  ))}
                </colgroup>
                <thead>
                  {/* Sort headers */}
                  <tr>
                    <th style={{ ...thStyle, width: '36px' }}>
                      <input type="checkbox" checked={selected.size === filteredLeads.length && filteredLeads.length > 0} onChange={selectAll} />
                    </th>
                    {orderedVisibleCols.map(col => (
                      <th key={col.key}
                        draggable
                        onDragStart={() => { dragColRef.current = col.key; }}
                        onDragOver={e => e.preventDefault()}
                        onDrop={() => reorderCols(dragColRef.current, col.key)}
                        style={{ ...thStyle, position: 'relative', width: colWidths[col.key] || COL_DEFAULT_WIDTHS[col.key] || 120, cursor: 'pointer' }}
                        title="Click to sort · double-click to fit the column to its contents"
                        onClick={() => handleHeaderClick(col.key)}
                        onDoubleClick={() => handleHeaderDoubleClick(col.key)}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', paddingRight: '10px' }}>
                          {colLabel(col)}
                          {sortField === col.key && <span style={{ marginLeft: '4px' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                          {sortField !== col.key && <span style={{ marginLeft: '4px', color: '#c5d8e8' }}>⇅</span>}
                        </span>
                        {/* Resize handle */}
                        <div
                          onMouseDown={e => startColResize(col.key, e)}
                          onClick={e => e.stopPropagation()}
                          onDoubleClick={e => { e.stopPropagation(); handleHeaderDoubleClick(col.key); }}
                          style={{ position: 'absolute', right: 0, top: '15%', bottom: '15%', width: '4px', cursor: 'col-resize', zIndex: 3, background: '#b8d4e8', borderRadius: '2px' }}
                        />
                      </th>
                    ))}
                  </tr>
                  {/* Filter inputs row */}
                  <tr>
                    <th style={{ background: '#eaf4fb', padding: '4px 6px', position: 'sticky', top: '33px', zIndex: 2 }}>
                      {filtersActive && (
                        <button onClick={clearAllFilters} style={{ fontSize: '10px', padding: '2px 6px', border: '1px solid #ccc', borderRadius: '3px', background: '#fff', cursor: 'pointer', color: '#888', whiteSpace: 'nowrap' }}>
                          ✕ Clear
                        </button>
                      )}
                    </th>
                    {orderedVisibleCols.map(col => (
                      <th key={col.key} style={{ background: '#eaf4fb', padding: '4px 6px', position: 'sticky', top: '33px', zIndex: 2 }}>
                        <input
                          value={colFilters[col.key] || ''}
                          onChange={e => setColFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                          placeholder="filter…"
                          title="Type text to show matching rows. Start with ! to hide them (e.g. !sl)"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '3px 6px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '3px', outline: 'none' }}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => {
                    const badCols   = flaggedCols(lead);
                    const hlStyle   = CELL_HIGHLIGHT[effectiveStatus(lead)] || {};
                    const cellStyle = (colKey) => ({
                      ...tdStyle,
                      ...(badCols.has(colKey) ? hlStyle : {}),
                    });
                    return (
                      <tr key={lead.id}>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <input type="checkbox" checked={selected.has(lead.id)} onChange={() => toggleSelect(lead.id)} onClick={e => e.stopPropagation()} />
                          </td>
                          {orderedVisibleCols.map(col => {
                            // ── Read-only cols ──────────────────────────────
                            if (col.key === 'lead_id') return <td key={col.key} style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '11px', color: '#555' }}>{lead.lead_id}</td>;
                            // Editable inline — click the badge to change PM Override directly.
                            // Saving takes effect on Lead.status immediately (see backend
                            // OVERRIDE_TO_STATUS), so the badge/tab counts reflect it at once.
                            if (col.key === 'qa_status') return (
                              <td key={col.key} style={tdStyle}>
                                <EditableCell
                                  type="select"
                                  value={lead.qa_override || ''}
                                  options={buildOverrideOptions(includesCallReview)}
                                  display={<QABadge lead={lead} includesCallReview={includesCallReview} />}
                                  onSave={(v) => handleOverrideSave(lead.id, v || null, lead.qa_override_note)}
                                />
                              </td>
                            );
                            if (col.key === 'qa_flags') return (
                              <td key={col.key} style={tdStyle}>
                                {lead.qa_flags && lead.qa_flags.length > 0 ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', overflow: 'hidden' }}>
                                    {lead.qa_flags.map((f, i) => {
                                      const chipStatus = effectiveStatus(lead);
                                      const chipStyle = chipStatus === 'Rejected'
                                        ? { background: '#fdedec', color: '#c0392b', border: '1px solid #f5b7b1' }
                                        : chipStatus === 'Needs Review'
                                        ? { background: '#fef9e7', color: '#b7770d', border: '1px solid #f0c040' }
                                        : chipStatus === 'Client Returned'
                                        ? { background: '#fef0e6', color: '#d35400', border: '1px solid #f5cba7' }
                                        : { background: '#f0f0f0', color: '#666', border: '1px solid #ddd' };
                                      return <span key={i} style={{ ...chipStyle, borderRadius: '4px', padding: '1px 6px', fontSize: '10px', whiteSpace: 'nowrap' }}>{f}</span>;
                                    })}
                                  </div>
                                ) : rejectReasonFlag(lead) ? (
                                  <span style={{ background: '#fdedec', color: '#c0392b', border: '1px solid #f5b7b1', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', whiteSpace: 'nowrap' }}>{rejectReasonFlag(lead)}</span>
                                ) : <span style={{ fontSize: '11px', color: '#aaa' }}>{effectiveStatus(lead) ? '—' : 'not run'}</span>}
                                {/* Inline override note — click to edit without expanding the row */}
                                <div style={{ marginTop: '2px' }}>
                                  <EditableCell
                                    value={lead.qa_override_note || ''}
                                    placeholder="Reason for override…"
                                    emptyText="+ note"
                                    display={lead.qa_override_note
                                      ? <span
                                          title={lead.qa_override_note_by_name ? `${lead.qa_override_note_by_name}${lead.qa_override_note_at ? ` — ${noteDate(lead.qa_override_note_at)}` : ''}` : undefined}
                                          style={{ fontSize: '10px', color: '#888', fontStyle: 'italic' }}
                                        >📝 {noteByline(lead)}{lead.qa_override_note}</span>
                                      : undefined}
                                    onSave={(v) => handleOverrideSave(lead.id, lead.qa_override, v)}
                                  />
                                </div>
                              </td>
                            );
                            if (col.key === 'supplier_delivery_date') return <td key={col.key} style={{ ...tdStyle, fontSize: '11px', color: '#777' }}>{lead.supplier_delivery_date || <span style={{ color: '#aaa' }}>—</span>}</td>;
                            if (col.key === 'optin_date') {
                              const badRaw = !lead.optin_date && lead.raw_data?._unparsed_dates?.optin_date;
                              return (
                                <td key={col.key} style={tdStyle}>
                                  <EditableCell
                                    type={badRaw ? 'text' : 'date'}
                                    value={lead.optin_date || badRaw || ''}
                                    display={badRaw
                                      ? <span style={{ color: '#c0392b' }} title="Could not parse this value on upload — click to fix">{badRaw}</span>
                                      : (lead.optin_date ? formatDateDisplay(lead.optin_date) : undefined)}
                                    onSave={v => handleEditField(lead.id, { optin_date: v })}
                                  />
                                </td>
                              );
                            }
                            if (col.key === 'digital_reg_datetime' || col.key === 'call_confirm_datetime') {
                              const badRaw = !lead[col.key] && lead.raw_data?._unparsed_dates?.[col.key];
                              return (
                                <td key={col.key} style={tdStyle}>
                                  <EditableCell
                                    value={lead[col.key] ? formatDateTimeDisplay(lead[col.key]) : (badRaw || '')}
                                    display={badRaw ? <span style={{ color: '#c0392b' }} title="Could not parse this value on upload — click to fix">{badRaw}</span> : undefined}
                                    placeholder="YYYY-MM-DD HH:MM"
                                    onSave={v => handleEditField(lead.id, { [col.key]: v })}
                                  />
                                </td>
                              );
                            }
                            // ── Marketo feedback (read-only — written by feedback-check, never
                            // hand-edited here) ─────────────────────────────
                            if (col.key === 'marketo_progression_status') return (
                              <td key={col.key} style={tdStyle}>{lead.marketo_progression_status || <span style={{ color: '#aaa' }}>—</span>}</td>
                            );
                            if (col.key === 'marketo_is_exhausted' || col.key === 'marketo_acquired_by' || col.key === 'marketo_reached_success') {
                              const v = lead[col.key];
                              return <td key={col.key} style={tdStyle}>{v === true ? 'Yes' : v === false ? 'No' : <span style={{ color: '#aaa' }}>—</span>}</td>;
                            }
                            if (col.key === 'marketo_membership_date') return (
                              <td key={col.key} style={{ ...tdStyle, fontSize: '11px', color: '#777' }}>
                                {lead.marketo_membership_date ? formatDateTimeDisplay(lead.marketo_membership_date) : <span style={{ color: '#aaa' }}>—</span>}
                              </td>
                            );
                            // ── Inline-editable cols ────────────────────────
                            if (col.key === 'name') return (
                              <td key={col.key} style={cellStyle('name')}>
                                <EditableCell
                                  value={`${lead.first_name || ''} ${lead.last_name || ''}`.trim()}
                                  placeholder="First Last"
                                  onSave={async (v) => {
                                    const parts = v.trim().split(/\s+/);
                                    const first = parts[0] || '';
                                    const last = parts.slice(1).join(' ') || '';
                                    await handleEditField(lead.id, { first_name: first, last_name: last });
                                  }}
                                />
                              </td>
                            );
                            if (col.key === 'email') return (
                              <td key={col.key} style={{ ...cellStyle('email'), fontSize: '11px' }}>
                                <EditableCell value={lead.email || ''} placeholder="email@example.com"
                                  onSave={v => handleEditField(lead.id, { email: v })} />
                              </td>
                            );
                            if (col.key === 'title_category') {
                              const cat = lead.title_category;
                              const catColors = { primary: '#1a7f4b', secondary: '#2c5aa0', reject: '#c0392b' };
                              const catBgs   = { primary: '#d4f5e5', secondary: '#eef4ff', reject: '#fdf0ee' };
                              return (
                                <td key={col.key} style={cellStyle('title_category')}>
                                  <EditableCell
                                    value={cat || ''}
                                    type="select"
                                    options={[
                                      { value: '', label: '— none —' },
                                      { value: 'primary',   label: 'Primary' },
                                      { value: 'secondary', label: 'Secondary' },
                                      { value: 'reject',    label: 'Reject' },
                                    ]}
                                    display={cat
                                      ? <span style={{ background: catBgs[cat], color: catColors[cat], borderRadius: '10px', padding: '1px 8px', fontSize: '11px', fontWeight: '600', border: `1px solid ${catColors[cat]}44` }}>{cat}</span>
                                      : null}
                                    onSave={v => handleEditField(lead.id, { title_category: v || null })}
                                  />
                                </td>
                              );
                            }
                            // LinkedIn — value stays plain editable text (the wrapping
                            // EditableCell click-to-edit already owns the whole cell), with
                            // a small "↗" opening it in a new tab, stopPropagation'd so that
                            // click doesn't also enter edit mode. Same icon-next-to-value
                            // pattern QARules.js's ListManagePanel uses for its own list
                            // "jump to List Management" links.
                            if (LINKEDIN_COLS[col.key]) {
                              const url = lead[col.key];
                              const normalizedUrl = url && !/^https?:\/\//i.test(url) ? `https://${url}` : url;
                              return (
                                <td key={col.key} style={cellStyle(col.key)}>
                                  <EditableCell
                                    value={url || ''}
                                    onSave={v => handleEditField(lead.id, { [col.key]: v })}
                                    display={url ? (
                                      <span>
                                        {/* Arrow first, so it stays visible when a long URL is cut off by the column width. */}
                                        <a href={normalizedUrl} target="_blank" rel="noopener noreferrer"
                                          onClick={e => e.stopPropagation()}
                                          title={LINKEDIN_COLS[col.key]}
                                          style={{ marginRight: '4px', color: '#3498db', textDecoration: 'none' }}>
                                          ↗
                                        </a>
                                        {url}
                                      </span>
                                    ) : undefined}
                                  />
                                </td>
                              );
                            }
                            // Call Notes and Campaign Notes both hold long, often
                            // multi-paragraph supplier text with line breaks between
                            // sections for readability — the generic single-line text
                            // cell below can't hold or render a newline at all, which
                            // silently stripped them on every edit. A real textarea
                            // preserves them (both while editing and in the pre-wrap
                            // read display) and gives more room to work than one
                            // cramped, horizontally-scrolling line.
                            if (LONG_TEXT_COLS.has(col.key)) {
                              return (
                                <td key={col.key} style={cellStyle(col.key)}>
                                  <EditableCell
                                    type="textarea"
                                    rows={8}
                                    value={lead[col.key] || ''}
                                    onSave={v => handleEditField(lead.id, { [col.key]: v })}
                                  />
                                </td>
                              );
                            }
                            // Generic editable text cell for all other contact columns
                            return (
                              <td key={col.key} style={cellStyle(col.key)}>
                                <EditableCell
                                  value={lead[col.key] || ''}
                                  onSave={v => handleEditField(lead.id, { [col.key]: v })}
                                />
                              </td>
                            );
                          })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
